import { BODY_FONT, BRAND, DISPLAY_FONT, MONO_FONT } from "../theme";
import { fallRiseLegendColors, fallOrangeRiseBlueLegendColors, heatmapSchemeColors, percentChangeLegendColors } from "./colorSchemes";
import { CRIME_TYPES } from "../crimeTypes";
import { moreListClass, moreRowClass, moreState, moreToggleCSS, moreToggleHTML } from "./moreToggle";
import { InsightsOutlined, KeyOutlined, MovingOutlined, PublicOutlined, ShieldOutlined, SwapHorizOutlined, WifiOutlined } from "@mui/icons-material";
import { coverageText, NETWORKS } from "../account/connectivityRules";

// The Big Picture area maps: whole-country maps that shade every area (a local
// authority, say) by one figure from a stats table, drawn by AreaMapLayer.js. Each
// map is an entry below; the server builds its GeoJSON from the ukmap.area_map row
// of the same mapName (sql/area_map.sql, served by /areamap). Each feature carries
// areacd, areanm (an MSOA's neighbourhood name) and that table's details json.
//
// An entry says:
//  - mapName, menuText, menuIcon, title: the server's map name, the menu's text and icon, and
//    the map's heading
//  - menuShortText: the menu's text on phones, where the Big Picture heading says the rest
//  - areaName / areaNamePlural: what one area is called ('local authority')
//  - controls: the settings the user can change, in order, each {key, label, options:{value:label}};
//    shown as pills in the popup and in the map's Filters panel. defaults gives each one's start
//  - palette: the colours a level is shaded in ('teal' unless given)
//  - getValue(details, opts): the figure to shade by, or null when there isn't one
//  - isChange(opts): true when the figure is a change, shaded from fell (rust) to rose (green),
//    or with changeViews from fell (blue) to rose (red)
//  - format(value, opts): the figure as text
//  - drillDown: the map of the smaller areas within one of its areas ({mapName, areaName,
//    areaNamePlural}; a row of ukmap.area_map with parentcodecol), whose details match its own.
//    parentCode(feature props) gives the code that map knows the area by, when not its areacd
//  - withinOnly: the map only shows the smaller areas of one area, given as the parent and
//    parentName settings (kept like the controls' settings, but not shown as pills); it has no
//    whole-country map and no way up to one
//  - listed: false keeps the map out of the menus (one opened from elsewhere, like withinOnly maps)
//  - mapSwitch: the key of a control also shown as a row of pills on the map, under its chip
//  - hoverTip: true shows each area's name and figure on hover (not on touch screens)
//  - legendEnds: the key's two end labels, in place of the lowest and highest figures
//  - changeViews: the map can switch between its figure and its % change over 1 year and
//    over the longest whole years up to 5 (CHANGE_VIEWS, shown on the map by AreaMapLayer.js).
//    changeYears(details) gives the years an area has changes for (the map offers those the
//    manifest lists, else those its areas have), changeView(opts) the view shown ('level',
//    '1y' or 'long', or null for another change) and setChangeView(view, longYears, opts) the
//    settings that show one. AreaMapLayer adds longYears (the long view's years) to opts
//  - sinceText(details, opts): for a change, the period it is over ('since 2023'), for the hover tip
//  - buildScale(values, opts): the map's own colours in place of buildScale's (the migration
//    map's purple to green, banded by size either way); legendRange(scale) the key's low,
//    middle and high labels
//  - controls' note: a line under the setting's pills in the Filters panel
//  - flows(details, opts): the places people moved between an area and, [{cd, nm, x, y, value,
//    dir:'in'|'out'}], drawn as arrows once the area is clicked (flowArrows.js), with the area's
//    card docked beside the map instead of a popup, and nationalHTML(features' props, opts,
//    isMobile) the card with no area picked (not on phones); signedFlows(opts) labels them
//    '+' and '−'; clickHint ends the chip's 'Click a council ...' hint
//  - legendSubtitle(opts, details of one area), popupHTML(feature props, opts, scale, isMobile, values, level): values
//    are every area's figure, and level says which areas are shown ({areaName, areaNamePlural,
//    within: the area they are in, or null, canDrillDown, drillAreaNamePlural, upAreaNamePlural})
//
// Another table becomes a map with an ukmap.area_map row, a line in GetAreaMap's
// whitelist, an entry here and its name in BaseLayer's areaMapLayers; the menus list
// every entry.

// Changes within this many percent either way count as no change
const NO_CHANGE_PCT = 0.5;

const has = (v) => v!==null && v!==undefined && !isNaN(v);

// The value each of the n-1 band boundaries falls at, so that each band holds about
// the same number of values
export const quantileEdges = (values, n) => {
    const sorted = values.filter(has).slice().sort((a, b) => a-b);
    if (sorted.length===0) return [];
    const edges = [];
    for (let i = 1; i<n; i++) {
        edges.push(sorted[Math.min(sorted.length-1, Math.floor(i*sorted.length/n))]);
    }
    return edges;
};

const bandFor = (v, edges) => {
    let i = 0;
    while (i<edges.length && v>=edges[i]) i++;
    return i;
};

// The colours for one setting of a map, from the values of all its areas. A level is
// split into equal-count teal bands, low to high, so London's prices don't squash the
// rest of the country into one or two shades. A change gets the price change
// colours: a pale middle for no change, then the falls and the rises each split into
// three equal-count bands, so the shading still differs where nearly every area rose.
// Returns {colors (the legend's strip, low to high), colorOf(value), min, max}, or
// null with nothing to shade. palette names the level's colours (MapColorStrategy's
// HEATMAP_SCHEMES). changeColors 'fallRise' shades a change blue to red rather than rust
// to green, and 'fallOrangeRiseBlue' orange to blue.
export const buildScale = (values, diverging, palette='teal', changeColors=null) => {
    const vals = values.filter(has);
    if (vals.length===0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (!diverging) {
        const colors = heatmapSchemeColors(palette);
        const edges = quantileEdges(vals, colors.length);
        return {colors, min, max, edges, bandOf:(v) => bandFor(v, edges), colorOf:(v) => colors[bandFor(v, edges)]};
    }
    const colors = changeColors==='fallRise' ? fallRiseLegendColors() :
        changeColors==='fallOrangeRiseBlue' ? fallOrangeRiseBlueLegendColors() : percentChangeLegendColors();
    const falls = vals.filter((v) => v<=-NO_CHANGE_PCT);
    const rises = vals.filter((v) => v>=NO_CHANGE_PCT);
    // Falls are banded by size, so the largest fall is the darkest
    const fallEdges = quantileEdges(falls.map((v) => -v), 3);
    const riseEdges = quantileEdges(rises, 3);
    const bandOf = (v) => {
        if (v<=-NO_CHANGE_PCT) return 2-bandFor(-v, fallEdges);
        if (v>=NO_CHANGE_PCT) return 4+bandFor(v, riseEdges);
        return 3;
    };
    return {colors, min, max, bandOf, colorOf:(v) => colors[bandOf(v)]};
};

const money = new Intl.NumberFormat('en-GB', {style:'currency', currency:'GBP', minimumFractionDigits:0, maximumFractionDigits:0});
const plain = new Intl.NumberFormat('en-GB');
const formatPct = (v) => (v>0 ? '+' : v<0 ? '−' : '')+(Math.round(Math.abs(v)*10)/10)+'%';
// n colours spread evenly along a strip of 'rgb(r,g,b)' colours, first to last
export const rampShades = (colors, n) => {
    const rgb = colors.map((c) => c.match(/\d+/g).map(Number));
    return Array.from({length:n}, (_, i) => {
        const t = n<=1 ? 0 : i*(rgb.length-1)/(n-1);
        const lo = Math.floor(t), hi = Math.min(lo+1, rgb.length-1), f = t-lo;
        return 'rgb('+rgb[lo].map((v, k) => Math.round(v+(rgb[hi][k]-v)*f)).join(',')+')';
    });
};
const sentenceCase = (str) => str ? str.charAt(0).toUpperCase()+str.slice(1) : '';
export const escapeHTML = (str) => String(str===null || str===undefined ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const formatMonYear = (dateStr) => {
    if (!dateStr) return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', {month:'short', year:'numeric', timeZone:'UTC'});
};

// --- The change views (a map's changeViews) ---

// The longest change the switch offers, in whole years
export const MAX_CHANGE_YEARS = 5;

// The views a map with changeViews offers, from the whole years its changes cover: the
// figure itself, its change over 1 year, and over the longest years up to 5 ('3 yr change'
// where the data goes back only 3 years). Each is {key, years, label, shortLabel}
export const changeViewsFor = (years) => {
    const ys = (years || []).map(Number).filter((y) => Number.isInteger(y) && y>=1 && y<=MAX_CHANGE_YEARS);
    const views = [{key:'level', years:null, label:'Absolute', shortLabel:'Absolute'}];
    if (ys.indexOf(1)>=0) views.push({key:'1y', years:1, label:'1 yr change', shortLabel:'1 yr'});
    const long = ys.filter((y) => y>1);
    if (long.length>0) {
        const n = Math.max(...long);
        views.push({key:'long', years:n, label:n+' yr change', shortLabel:n+' yr'});
    }
    return views;
};

// The change years of a map's areas, together (each area's from def.changeYears)
export const changeYearsOfAreas = (def, detailsList) => {
    const years = new Set();
    detailsList.forEach((d) => (def.changeYears(d) || []).forEach((y) => years.add(Number(y))));
    return Array.from(years).sort((a, b) => a-b);
};

// The long view's years for the settings (opts.changeYears, added by AreaMapLayer), or null
const longYearsOf = (opts) => {
    const view = changeViewsFor(opts && opts.changeYears).find((v) => v.key==='long');
    return view ? view.years : null;
};

// Crime's and rents' change periods for an area (details.change.periods, keyed by years),
// and the one a view shows: 'long' is the settings' long view, else the area's longest
const changePeriods = (details) => (details && details.change && details.change.periods) || {};
const periodYears = (details) => Object.keys(changePeriods(details)).map(Number).filter((y) => Number.isInteger(y) && y>=1);
const periodFor = (details, opts) => {
    const view = opts && opts.change;
    if (view==='1y') return changePeriods(details)['1'] || null;
    if (view!=='long') return null;
    let years = longYearsOf(opts);
    if (!years) {
        const ys = periodYears(details).filter((y) => y>1 && y<=MAX_CHANGE_YEARS);
        years = ys.length>0 ? Math.max(...ys) : null;
    }
    return years ? (changePeriods(details)[String(years)] || null) : null;
};
const periodPct = (period, key) => {
    const v = period && period.pct ? period.pct[key] : null;
    return has(v) ? Number(v) : null;
};
// How far back a change view's figures are compared: 'a year', '3 years'
const changeSpanText = (opts) => opts.change==='1y' ? 'a year' : longYearsOf(opts) ? longYearsOf(opts)+' years' : 'some years';
const isViewChange = (opts) => !!opts.change && opts.change!=='level';
// A rise is red and a fall blue in the change views' popups (.up and .down)
const riseClass = (v) => v>=NO_CHANGE_PCT ? 'up' : v<=-NO_CHANGE_PCT ? 'down' : '';
const changeLegendText = ' · blue fell, red rose';
const changeLegendTextOrange = ' · orange fell, blue rose';

// The views as a row of pills for the popup (the 'change' setting)
const changePillsHTML = (opts) => {
    const views = changeViewsFor(opts.changeYears);
    if (views.length<2) return '';
    return pillsHTML('View', 'change', Object.fromEntries(views.map((v) => [v.key, v.label])), opts.change);
};
// The hidden 'change' control of the crime and rent maps: its options are the views' keys,
// labelled on the map from the years the data has
const CHANGE_CONTROL = {key:'change', label:'View', hidden:true, options:{level:'Absolute', '1y':'1 yr change', long:'Longer change'}};

// A larger area's name and its figure, for a row's note: the figure kept on the same line
// as the name's last word, so a narrow popup wraps the name rather than the figure alone
const nameWithFigure = (name, figure) => escapeHTML(name).replace(/ (?=\S+$)/, '&nbsp;')+'&nbsp;'+figure;

// The popup's look, shared by the area maps: the same card as the Price Distribution
// and sale popups (MapDataLayer.js, PropertySalesLayer.js)
export const areaMapPopupCSS = (isMobile) => {
    const fontScale = isMobile ? 1.1 : 1;
    return '<style>'+
        '.ol-popup {min-width:'+(isMobile ? 260 : 300)+'px !important; width:auto !important; max-width:330px !important; opacity:1 !important; padding:12px 14px !important; '+
            'border-color:'+BRAND.line+' !important; box-shadow:0 10px 24px -12px rgba(20,35,31,.45) !important;} '+
        '.areaMapPopup {font-family:'+BODY_FONT+'; color:'+BRAND.ink+'; font-size:'+(16*fontScale)+'px; line-height:normal; padding-right:6px; display:grid; grid-template-columns:minmax(0, 1fr); gap:6px; '+
            'max-height:'+(isMobile ? 380 : 470)+'px; overflow-x:hidden; overflow-y:auto;} '+
        '.areaMapPopup .tag {font-family:'+MONO_FONT+'; font-size:.68em; letter-spacing:.06em; text-transform:uppercase; color:'+BRAND.mute+'; padding-right:18px;} '+
        '.areaMapPopup .name {font-family:'+DISPLAY_FONT+'; font-size:1.05em; font-weight:700; line-height:1.15; margin-top:-2px;} '+
        '.areaMapPopup .big {font-family:'+DISPLAY_FONT+'; font-size:'+(isMobile ? '1.1em' : '1.4em')+'; font-weight:700; font-variant-numeric:tabular-nums; line-height:1.05;} '+
        '.areaMapPopup .big small {font-size:.55em; font-weight:500; color:'+BRAND.mute+';} '+
        // A change: a rise red and a fall blue, as the change views shade them
        '.areaMapPopup .big.up {color:#a3202f;} .areaMapPopup .big.down {color:#1d5c99;} '+
        '.areaMapPopup .sub {font-size:.8em; color:'+BRAND.mute+'; margin-top:-2px;} '+
        '.areaMapPopup .scale {display:flex; height:10px; border-radius:5px; overflow:hidden; margin-top:2px;} '+
        '.areaMapPopup .scale i {display:block; flex:1; height:100%;} '+
        '.areaMapPopup .scale i.here {box-shadow:inset 0 0 0 2px '+BRAND.ink+';} '+
        '.areaMapPopup .hint {font-size:.68em; color:'+BRAND.mute+'; display:flex; justify-content:space-between; gap:8px;} '+
        '.areaMapPopup .rank {font-size:.74em; font-weight:500; background:'+BRAND.tint+'; border-radius:6px; padding:5px 8px;} '+
        // The label in its own column, so pills that wrap line up under the first pill, not the label
        '.areaMapPopup .pills {display:flex; gap:3px; align-items:flex-start;} '+
        '.areaMapPopup .pills .opts {display:flex; flex-wrap:wrap; gap:3px; align-items:center; flex:1; min-width:0;} '+
        '.areaMapPopup .pills span {font-size:.7em; font-weight:500; border:1px solid '+BRAND.line+'; border-radius:999px; padding:2px 8px; background:#fff; white-space:nowrap; cursor:pointer;} '+
        '.areaMapPopup .pills span.on {background:'+BRAND.ink+'; color:#fff; border-color:'+BRAND.ink+'; cursor:default;} '+
        // The £ | % pair, set a little apart from the periods by a spacer that stays at the
        // end of the line when the pair wraps, so it then lines up under the periods
        '.areaMapPopup .pills .sep {width:5px; flex:none;} '+
        '.areaMapPopup .pills .group {display:inline-flex;} '+
        '.areaMapPopup .pills .group span {min-width:14px; padding:3px 12px; font-size:.8em; font-weight:600; text-align:center;} '+
        '.areaMapPopup .pills .group span:first-child {border-radius:999px 0 0 999px;} '+
        '.areaMapPopup .pills .group span:last-child {border-radius:0 999px 999px 0; border-left:none;} '+
        '.areaMapPopup .pills b {font-family:'+MONO_FONT+'; font-size:.6em; font-weight:500; letter-spacing:.06em; text-transform:uppercase; color:'+BRAND.mute+'; min-width:48px; flex:none; line-height:2.1;} '+
        '.areaMapPopup .head {font-family:'+MONO_FONT+'; font-size:.6em; letter-spacing:.06em; text-transform:uppercase; color:'+BRAND.mute+'; margin-top:2px;} '+
        '.areaMapPopup .changes {display:grid; grid-template-columns:repeat(4, minmax(0, 1fr)); gap:3px;} '+
        '.areaMapPopup .changes span {display:grid; justify-items:center; border:1px solid '+BRAND.line+'; border-radius:6px; padding:3px 2px; cursor:pointer;} '+
        '.areaMapPopup .changes span.on {border-color:'+BRAND.ink+'; box-shadow:inset 0 0 0 1px '+BRAND.ink+'; cursor:default;} '+
        '.areaMapPopup .changes small {font-family:'+MONO_FONT+'; font-size:.6em; color:'+BRAND.mute+'; text-transform:uppercase; letter-spacing:.05em;} '+
        '.areaMapPopup .changes em {font-style:normal; font-size:.78em; font-weight:600; font-variant-numeric:tabular-nums; white-space:nowrap;} '+
        '.areaMapPopup em.up {color:#a3202f;} .areaMapPopup em.down {color:#1d5c99;} .areaMapPopup em.na {color:'+BRAND.mute+'; font-weight:400;} '+
        '.areaMapPopup .rows {display:grid; grid-template-columns:auto minmax(0, 1fr); font-size:.78em;} '+
        '.areaMapPopup .rows > span {padding:4px 6px;} '+
        '.areaMapPopup .rows .val {display:grid; justify-items:end; text-align:right; min-width:0;} '+
        '.areaMapPopup .rows b {font-weight:600; font-variant-numeric:tabular-nums;} '+
        '.areaMapPopup .rows b.up, .areaMapPopup .keys b.up {color:#a3202f;} .areaMapPopup .rows b.down, .areaMapPopup .keys b.down {color:#1d5c99;} '+
        // House prices and rents: a fall in orange and a rise in blue, as their maps shade them
        '.areaMapPopup.orange .big.up, .areaMapPopup.orange em.up, .areaMapPopup.orange .rows b.up {color:#1d5c99;} '+
        '.areaMapPopup.orange .big.down, .areaMapPopup.orange em.down, .areaMapPopup.orange .rows b.down {color:#a14e05;} '+
        '.areaMapPopup .rows small {color:'+BRAND.mute+'; font-size:.88em;} '+
        '.areaMapPopup .rows .chosen {background:'+BRAND.tint+';} '+
        '.areaMapPopup .rows .type.chosen {border-radius:6px 0 0 6px; font-weight:600;} '+
        '.areaMapPopup .rows .val.chosen {border-radius:0 6px 6px 0;} '+
        '.areaMapPopup .scale.marked {position:relative; overflow:visible; margin:6px 0 4px;} '+
        '.areaMapPopup .scale.marked i:first-child {border-radius:5px 0 0 5px;} '+
        '.areaMapPopup .scale.marked i:last-of-type {border-radius:0 5px 5px 0;} '+
        '.areaMapPopup .mark {display:inline-block; box-sizing:border-box;} '+
        '.areaMapPopup .scale .mark {position:absolute; top:50%; transform:translate(-50%, -50%);} '+
        '.areaMapPopup .mark.here {width:4px; height:18px; border-radius:2px; background:'+BRAND.ink+'; box-shadow:0 0 0 1.5px #fff;} '+
        '.areaMapPopup .mark.ring {width:11px; height:11px; border-radius:50%; background:#fff; border:2.5px solid '+BRAND.ink+';} '+
        '.areaMapPopup .mark.dot {width:9px; height:9px; transform:translate(-50%, -50%) rotate(45deg); background:'+BRAND.ink+'; box-shadow:0 0 0 1.5px #fff;} '+
        '.areaMapPopup .markkey .mark {position:static; transform:none; margin-right:5px; vertical-align:-1px;} '+
        '.areaMapPopup .markkey .mark.here {height:11px;} '+
        '.areaMapPopup .markkey .mark.dot {transform:rotate(45deg); width:8px; height:8px;} '+
        '.areaMapPopup .markkey {display:flex; flex-wrap:wrap; gap:3px 12px; font-size:.72em; color:'+BRAND.mute+';} '+
        '.areaMapPopup .markkey em {font-style:normal; font-weight:600; color:'+BRAND.ink+'; font-variant-numeric:tabular-nums;} '+
        '.areaMapPopup [data-category] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
        '.areaMapPopup [data-category]:hover {text-decoration-color:'+BRAND.ink+';} '+
        '.areaMapPopup [data-domain] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
        '.areaMapPopup [data-domain]:hover {text-decoration-color:'+BRAND.ink+';} '+
        '.areaMapPopup .stack {display:flex; height:10px; border-radius:5px; overflow:hidden;} '+
        '.areaMapPopup .stack i {display:block; height:100%;} '+
        '.areaMapPopup .keys {display:grid; gap:3px; font-size:.78em;} '+
        '.areaMapPopup .keys div {display:grid; grid-template-columns:auto minmax(0, 1fr) auto; gap:7px; align-items:center;} '+
        '.areaMapPopup .keys i {width:9px; height:9px; border-radius:2px; display:block; box-shadow:inset 0 0 0 1px rgba(122,42,28,0.25);} '+
        '.areaMapPopup .keys div {padding:2px 6px; border-radius:6px;} '+
        '.areaMapPopup .keys div.chosen {background:'+BRAND.tint+'; font-weight:600;} '+
        '.areaMapPopup .keys i.blank {box-shadow:none;} '+
        '.areaMapPopup .keys .val {display:grid; justify-items:end; text-align:right;} '+
        '.areaMapPopup .keys small {color:'+BRAND.mute+'; font-size:.88em; font-weight:400; white-space:nowrap;} '+
        '.areaMapPopup .keys b {font-weight:600; font-variant-numeric:tabular-nums;} '+
        moreToggleCSS('.areaMapPopup')+
        '.areaMapPopup .title {display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:4px 8px; margin-top:-2px;} '+
        '.areaMapPopup .title .name {margin-top:0; min-width:0; overflow-wrap:anywhere;} '+
        '.areaMapPopup .drill {font-size:.7em; font-weight:600; color:#fff; background:'+BRAND.ink+'; border-radius:999px; padding:3px 10px; white-space:nowrap; cursor:pointer;} '+
        '.areaMapPopup .drill svg {display:inline-block; vertical-align:-1px; margin-right:5px;} '+
        '.areaMapPopup .drill.up {color:'+BRAND.ink+'; background:#fff; border:1px solid '+BRAND.line+';} '+
        '.areaMapPopup .drill:hover {background:'+BRAND.green+'; color:#fff; border-color:'+BRAND.green+';} '+
        '.areaMapPopup .none {font-size:.8em; color:'+BRAND.mute+';} '+
        '.areaMapPopup .source {font-size:.68em; color:'+BRAND.mute+'; border-top:1px solid '+BRAND.line+'; padding-top:6px; margin-top:2px;} '+
        '</style>';
};

// Where a value sits on the map's scale: the strip of shades with its own marked
const scaleHTML = (scale, value) => {
    if (!scale || !has(value)) return '';
    const here = scale.bandOf(value);
    return '<div class="scale">'+scale.colors.map((c, i) => '<i'+(i===here ? ' class="here"' : '')+' style="background:'+c+'"></i>').join('')+'</div>';
};

// A boundary split into smaller areas, for the button that breaks an area down into the
// smaller areas within it
const BREAKDOWN_ICON = '<svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">'+
    '<path d="M2 2.5h12v11H2z M2 7.5h6 M8 2.5v11 M8 9.5h6 M11 9.5v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

// An arrow turning up and back, as for going up to a parent folder: for the button that
// goes from the smaller areas back up to the larger ones
const LEVEL_UP_ICON = '<svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">'+
    '<path d="M13 14V8.5a2 2 0 0 0-2-2H3.5 M6.5 3 3 6.5 6.5 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// The area's name, with the button beside it (or under it, when there isn't room) that
// zooms in to the smaller areas within it, or back out from them
const titleHTML = (name, level) => {
    let button = '';
    if (level && level.canDrillDown) {
        button = '<span class="drill" data-drill role="button" title="See the '+level.drillAreaNamePlural+' in '+escapeHTML(name)+'">'+
            BREAKDOWN_ICON+sentenceCase(level.drillAreaNamePlural)+' →</span>';
    }
    // Opened from a local area's map: back to it, rather than up to the larger areas
    else if (level && level.within && level.canReturn) {
        button = '<span class="drill up" data-return role="button" title="Back to the local area map">'+LEVEL_UP_ICON+'Back to local area</span>';
    }
    else if (level && level.within && level.upAreaNamePlural) {
        button = '<span class="drill up" data-drill-up role="button" title="Back to all '+level.upAreaNamePlural+'">'+LEVEL_UP_ICON+
            sentenceCase(level.upAreaNamePlural)+'</span>';
    }
    return '<div class="title"><span class="name">'+escapeHTML(name)+'</span>'+button+'</div>';
};

// Where along the scale a value falls, from 0 (lowest) to 1 (highest): within its band,
// in proportion to the band's own range, as the bands are equal-count rather than equal width
const scalePosition = (scale, v) => {
    const n = scale.colors.length;
    const i = scale.bandOf(v);
    const bounds = [scale.min].concat(scale.edges || []).concat([scale.max]);
    const lo = bounds[i], hi = bounds[i+1];
    const frac = (has(lo) && has(hi) && hi>lo) ? Math.min(1, Math.max(0, (v-lo)/(hi-lo))) : 0.5;
    return (i+frac)/n;
};

// The map's scale with this area and the larger areas it is in (props.compare: its region
// and country, {name, details} each) marked on it, the scale's ends (hint) and a key naming
// each with its figure
const MARKERS = ['here', 'ring', 'dot'];
const compareScaleHTML = (props, scale, value, getValue, format, hint) => {
    const compare = (props.compare || []).map((c) => ({name:c.name, value:getValue(c.details)})).filter((c) => has(c.value));
    const items = [{name:props.areanm, value}].concat(compare).map((c, i) => ({...c, marker:MARKERS[Math.min(i, MARKERS.length-1)]}));
    let html = '<div class="scale marked">'+scale.colors.map((c) => '<i style="background:'+c+'"></i>').join('')+
        items.map((c) => '<b class="mark '+c.marker+'" style="left:'+(100*scalePosition(scale, c.value)).toFixed(1)+'%"></b>').join('')+'</div>'+hint;
    html += '<div class="markkey">'+items.map((c) => '<span><b class="mark '+c.marker+'"></b>'+escapeHTML(c.name)+' <em>'+format(c.value)+'</em></span>').join('')+'</div>';
    return html;
};

// A row of pills for one of a map's controls; the chosen one dark
const pillsHTML = (label, key, options, value) =>
    '<div class="pills"><b>'+label+'</b><div class="opts">'+Object.keys(options).map((k) =>
        '<span data-'+key+'="'+k+'"'+(k===value ? ' class="on"' : '')+' role="button">'+options[k]+'</span>').join('')+'</div></div>';

// --- Sold prices by local authority (ukmap.property_lad) ---

const PRICE_METRICS = {
    medpricepersqm: {pill:'Per m²', name:'median price per m²', unit:'per m²'},
    medprice: {pill:'Price', name:'median price', unit:''},
    medarea: {pill:'Size', name:'median size', unit:'m²'},
    numsales: {pill:'Sales', name:'number of sales', unit:'sales'},
};
// A period is a key of the details json: the price over the last 1 or 5 years
// ('full'), or the change in it. A change compares the last 12 months with the same
// 12 months 1, 3, 5 or 10 years earlier.
const PRICE_PERIODS = {
    '1y':'Last year', full:'Last 5 years',
    '1y_chg':'1 year change', '3y_chg':'3 year change', '5y_chg':'5 year change', '10y_chg':'10 year change',
};
const PRICE_LEVEL_TEXT = {'1y':'in the last year', full:'over the last 5 years'};
const PRICE_CHANGE_TEXT = {'1y_chg':'1 year', '3y_chg':'3 years', '5y_chg':'5 years', '10y_chg':'10 years'};
const PRICE_TYPES = {all:'All homes', houses:'Houses', D:'Detached', S:'Semi-detached', T:'Terraced', F:'Flats'};

const isPriceChange = (opts) => opts.period.indexOf('_chg')>=0;
// A level's number of sales is numsalestotal; in a change, numsales is the change in
// the number of sales (numsalestotal there is still the last year's count)
const priceKey = (metric, isChange) => (metric==='numsales' && !isChange) ? 'numsalestotal' : metric;
const priceValue = (details, period, category, metric) => {
    const data = details && details[period] && details[period][category];
    const v = data ? data[priceKey(metric, period.indexOf('_chg')>=0)] : null;
    return has(v) ? Number(v) : null;
};
const formatPriceLevel = (v, metric) => (metric==='numsales' || metric==='medarea') ? plain.format(v) : money.format(v);

// The Over row: the last year or 5 years, then, set a little apart, whether that is the
// figure itself (£, or # for size and sales) or its % change. The periods with only a
// change (3 and 10 years) light neither period, and going back to the figure from one
// takes the nearer of the two.
const OVER_PERIODS = {
    '1y':{level:'1y', change:'1y_chg'}, full:{level:'full', change:'5y_chg'},
};
const OVER_FOR = {'1y':'1y', '1y_chg':'1y', '3y_chg':'1y', full:'full', '5y_chg':'full', '10y_chg':'full'};
// The change views (AreaMapLayer's switch) are the period's 1 year and longest change
// up to 5 years; the figure itself is the period nearest the change shown
const priceChangeYears = (details) => Object.keys(details || {}).map((k) => k.match(/^(\d+)y_chg$/))
    .filter(Boolean).map((m) => Number(m[1]));
const priceChangeView = (opts) => {
    if (!isPriceChange(opts)) return 'level';
    if (opts.period==='1y_chg') return '1y';
    const long = longYearsOf(opts);
    return long && opts.period===long+'y_chg' ? 'long' : null;
};
const setPriceChangeView = (view, longYears, opts) => {
    if (view==='1y') return {period:'1y_chg'};
    if (view==='long' && longYears) return {period:longYears+'y_chg'};
    return {period:isPriceChange(opts) ? (OVER_FOR[opts.period] || '1y') : opts.period};
};
// When a change is from: the start of its earlier 12 months' end ('since Aug 2021')
const priceSince = (details, opts) => {
    const data = details && details[opts.period] && details[opts.period][opts.category];
    return data && data.mindate ? 'since '+formatMonYear(data.mindate) : '';
};

const overPillsHTML = (period, metric) => {
    const isChange = period.indexOf('_chg')>=0;
    const over = OVER_FOR[period] || '1y';
    const pill = (value, label, on, title) => '<span data-period="'+value+'"'+(on ? ' class="on"' : '')+' role="button"'+
        (title ? ' title="'+title+'"' : '')+'>'+label+'</span>';
    const exact = (k) => period===OVER_PERIODS[k].level || period===OVER_PERIODS[k].change;
    const levelSign = (metric==='medarea' || metric==='numsales') ? '#' : '£';
    return '<div class="pills"><b>Over</b><div class="opts">'+
        pill(isChange ? OVER_PERIODS['1y'].change : '1y', 'Last year', exact('1y'))+
        pill(isChange ? OVER_PERIODS.full.change : 'full', '5 years', exact('full'))+
        '<i class="sep"></i><div class="group">'+pill(OVER_PERIODS[over].level, levelSign, !isChange, 'Show the '+(levelSign==='£' ? 'price' : 'figure'))+
        pill(OVER_PERIODS[over].change, '%', isChange, 'Show the % change')+'</div></div></div>';
};

const propertyLadPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details;
    const {metric, period, category} = opts;
    const isChange = isPriceChange(opts);
    const m = PRICE_METRICS[metric];
    const typeName = PRICE_TYPES[category].toLowerCase();
    const value = priceValue(details, period, category, metric);

    let html = areaMapPopupCSS(isMobile)+'<div class="areaMapPopup orange">';
    html += '<span class="tag">Sold prices · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);
    // Just the figure: what it is shows in the pills, and the sales in the rows below
    if (!has(value)) {
        html += '<span class="none">Not enough sales of '+typeName+' here to show this. Try another home type or period.</span>';
    }
    else if (isChange) {
        const since = priceSince(details, opts);
        html += '<span class="big '+(value<0 ? 'down' : 'up')+'">'+formatPct(value)+(since ? ' <small>'+since+'</small>' : '')+'</span>';
        html += scaleHTML(scale, value);
        html += '<div class="hint"><span>Fell most '+formatPct(scale.min)+'</span><span>Rose most '+formatPct(scale.max)+'</span></div>';
    }
    else {
        html += '<span class="big">'+formatPriceLevel(value, metric)+(m.unit ? ' <small>'+m.unit+'</small>' : '')+'</span>';
        html += scaleHTML(scale, value);
        html += '<div class="hint"><span>Lowest '+formatPriceLevel(scale.min, metric)+'</span><span>Highest '+formatPriceLevel(scale.max, metric)+'</span></div>';
    }

    html += pillsHTML('Show', 'metric', Object.fromEntries(Object.keys(PRICE_METRICS).map((k) => [k, PRICE_METRICS[k].pill])), metric);
    html += overPillsHTML(period, metric);

    // The change over each period for the chosen home type; picking one maps it
    html += '<span class="head">'+sentenceCase(m.name.replace('median ', ''))+' change · '+typeName+'</span><div class="changes">'+
        Object.keys(PRICE_CHANGE_TEXT).map((p) => {
            const c = priceValue(details, p, category, metric);
            return '<span data-period="'+p+'"'+(p===period ? ' class="on"' : '')+' role="button" title="The last 12 months against the same 12 months '+
                PRICE_CHANGE_TEXT[p]+' earlier"><small>'+p.replace('_chg', '')+'</small>'+
                (has(c) ? '<em class="'+(c<0 ? 'down' : 'up')+'">'+(c<0 ? '▼ ' : '▲ ')+(Math.round(Math.abs(c)*10)/10)+'%</em>' : '<em class="na">–</em>')+'</span>';
        }).join('')+'</div>';

    // The figure being mapped for each home type sold here; picking one maps it
    const rows = Object.keys(PRICE_TYPES).filter((k) => k!=='houses')
        // numsalestotal is the count of sales, also in a change (where numsales is its change)
        .map((k) => ({key:k, val:priceValue(details, period, k, metric),
            numSales:(details && details[period] && details[period][k]) ? details[period][k].numsalestotal : null}))
        .filter((row) => has(row.val));
    if (rows.length>0) {
        // Each home type's share of all the sales, beside its count
        const allSales = priceValue(details, period, 'all', 'numsales');
        const shareOf = (row) => (row.key!=='all' && has(row.numSales) && allSales>0) ? Math.round(100*row.numSales/allSales)+'%' : null;
        html += '<span class="head">By home type · '+PRICE_PERIODS[period].toLowerCase()+'</span><div class="rows">';
        rows.forEach((row) => {
            const chosen = row.key===category ? ' chosen' : '';
            const share = shareOf(row);
            let note = '';
            if (!isChange && metric==='numsales') {
                if (share) note = share+' of sales';
            }
            else if (has(row.numSales)) {
                // A change has the last year's sales under it, without the shares
                note = plain.format(row.numSales)+' sales'+(share && !isChange ? ' · '+share : '');
            }
            html += '<span class="type'+chosen+'"><span data-category="'+row.key+'" role="button">'+PRICE_TYPES[row.key]+'</span></span>'+
                '<span class="val'+chosen+'"><b>'+(isChange ? formatPct(row.val) : formatPriceLevel(row.val, metric))+'</b>'+
                (note ? '<small>'+note+'</small>' : '')+'</span>';
        });
        html += '</div>';
    }

    const dates = details && (details[period] || {})[category];
    html += '<div class="source">Source: HM Land Registry.'+
        ((dates && dates.mindate && dates.maxdate) ? ' Sales from '+formatMonYear(dates.mindate)+' to '+formatMonYear(dates.maxdate)+'.' : '')+
        ' Prices are the median of all sales in the period. Changes compare the last 12 months with the same 12 months years earlier.</div>';
    html += '</div>';
    return html;
};

// --- Crime by local authority (ukmap.crime_lad) ---

const CRIME_METRICS = {
    numcrimesper100: {pill:'Per 100 people', name:'crimes per 100 people'},
    numcrimes: {pill:'Number', name:'crimes recorded'},
};
// What the map can show: all crimes, or one crime type. ukmap.crime_lad leaves out the
// types the crime tables don't count (create_crimelsoa in load_crime.sql)
const CRIME_LAD_EXCLUDED_TYPES = ['Bicycle theft', 'Shoplifting', 'Other crime'];
const CRIME_CATEGORIES = {total:'All crimes',
    ...Object.fromEntries(CRIME_TYPES.filter((t) => CRIME_LAD_EXCLUDED_TYPES.indexOf(t)<0).map((t) => [t, t]))};

// All crimes' or one crime type's figures for an area, or null where there are none (the
// police in Scotland don't publish street-level crime, so its areas have no people counted)
// An area whose police force skipped months (total.unpublished, crime_coverage in
// load_crime.sql) has none either: what it has are mostly the neighbouring force's crimes.
// Rates divide by the people who live or work there (numworkday, ukmap.crimepop in
// crime_workday.sql), or the residents in figures built before it
const crimeFigures = (details, category) => {
    const total = details && details.total;
    if (!total || total.unpublished || !has(total.numpeople) || Number(total.numpeople)<=0) return null;
    if (category==='total') return total;
    const numcrimes = (total.crimetypes && total.crimetypes[category]) || 0;
    const people = Number(has(total.numworkday) && Number(total.numworkday)>0 ? total.numworkday : total.numpeople);
    return {numcrimes, numpeople:total.numpeople, numworkday:total.numworkday, mindate:total.mindate, maxdate:total.maxdate,
        numcrimesper100:Math.round(10000*numcrimes/people)/100};
};
// In a change view, the % change in the number of crimes, the same as in the rate (the
// population is the same latest estimate for both)
const crimeValue = (details, opts) => {
    if (isViewChange(opts)) return periodPct(periodFor(details, opts), opts.category);
    const fig = crimeFigures(details, opts.category);
    const v = fig ? fig[opts.metric] : null;
    return has(v) ? Number(v) : null;
};
const formatCrime = (v, metric) => metric==='numcrimes' ? plain.format(v) : String(Math.round(v*10)/10);
const crimeName = (category) => category==='total' ? 'all crimes' : category.toLowerCase();

// The larger areas compared whose figures leave out a police force that skipped months
// (total.leftout, adjust_crime_area_coverage in load_crime.sql): ' North West leaves out
// Greater Manchester, as its police don't publish every month.'
const leftOutText = (compare) => (compare || []).filter((c) => c.details && c.details.total && Array.isArray(c.details.total.leftout) &&
    c.details.total.leftout.length>0).map((c) => ' '+escapeHTML(c.name)+' leaves out '+escapeHTML(c.details.total.leftout.join(' and ').replace(/ & /g, ' and '))+
    ', as '+(c.details.total.leftout.length>1 ? 'their' : 'its')+' police haven\'t published every month.').join('');

const crimePeriod = (fig) => (fig && fig.mindate && fig.maxdate) ? (fig.mindate===fig.maxdate ? fig.maxdate : fig.mindate+' to '+fig.maxdate) : '';

// A crime change view's popup body: the change in the crimes of the chosen type, against
// the region and country, then every crime type's change
const crimeChangeHTML = (props, opts, scale, level) => {
    const details = props.details || {};
    const {category} = opts;
    const change = details.change || {};
    const period = periodFor(details, opts);
    if (!period) {
        // A police force that skipped months says which (crime_coverage in load_crime.sql)
        const note = change.unpublished || (details.total && details.total.unpublished);
        return '<span class="none">No change figures here. '+escapeHTML(note || 'The police force\'s figures are missing months.')+'</span>'+changePillsHTML(opts);
    }
    const value = periodPct(period, category);
    let html = '';
    if (!has(value)) {
        html += '<span class="none">Too few '+crimeName(category)+' here in the 12 months to '+escapeHTML(period.maxdate)+' to compare.</span>';
    }
    else {
        html += '<span class="big '+riseClass(value)+'">'+formatPct(value)+' <small>since '+escapeHTML(period.maxdate)+'</small></span>';
        html += '<span class="sub">'+sentenceCase(crimeName(category))+' in the 12 months to '+escapeHTML(change.maxdate)+
            ', against the 12 months to '+escapeHTML(period.maxdate)+'</span>';
        const hint = '<div class="hint"><span>Fell most '+formatPct(scale.min)+'</span><span>Rose most '+formatPct(scale.max)+'</span></div>';
        if (!level.within && (props.compare || []).length>0) {
            html += compareScaleHTML(props, scale, value, (d) => crimeValue(d, opts), formatPct, hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
    }
    if (has(change.num) && has(period.num)) {
        html += '<span class="rank">'+plain.format(period.num)+' crimes in the 12 months to '+escapeHTML(period.maxdate)+', '+
            plain.format(change.num)+' in the 12 months to '+escapeHTML(change.maxdate)+'</span>';
    }
    html += changePillsHTML(opts);
    // Every crime type's change, most crimes now first; picking one maps it
    const pct = period.pct || {};
    const now = (details.total && details.total.crimetypes) || {};
    const types = Object.keys(pct).filter((k) => k!=='total' && CRIME_CATEGORIES.hasOwnProperty(k))
        .sort((a, b) => (now[b] || 0)-(now[a] || 0) || a.localeCompare(b));
    const row = (k, extra='') => {
        const v = pct[k];
        const cls = [k===category ? 'chosen' : '', extra].filter(Boolean).join(' ');
        return '<div'+(cls ? ' class="'+cls+'"' : '')+'><i class="blank"></i><span data-category="'+escapeHTML(k)+'" role="button">'+escapeHTML(CRIME_CATEGORIES[k] || k)+'</span>'+
            '<span class="val"><b class="'+riseClass(v)+'">'+formatPct(v)+'</b></span></div>';
    };
    html += '<span class="head">Crime types · change since '+escapeHTML(period.maxdate)+'</span>';
    const more = moreState(types.length, types.indexOf(category));
    html += '<div class="keys'+moreListClass(more)+'">'+(has(pct.total) ? row('total') : '')+
        types.map((k, i) => row(k, moreRowClass(i, more))).join('')+
        '</div>'+moreToggleHTML(types.length, more);
    html += '<div class="source">Source: data.police.uk. The crimes recorded in the 12 months to '+escapeHTML(change.maxdate)+
        ' against the same 12 months '+changeSpanText(opts)+' earlier. Types with under 20 crimes then are not compared.</div>';
    return html;
};

const crimeLadPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details;
    const {metric, category} = opts;
    if (isViewChange(opts)) {
        let html = areaMapPopupCSS(isMobile)+'<div class="areaMapPopup">';
        html += '<span class="tag">Crime change · '+level.areaName+'</span>';
        html += titleHTML(props.areanm, level);
        const total = details && details.total;
        if (!total || !has(total.numpeople)) {
            html += '<span class="none">No street-level crime figures are published here. Police Scotland does not publish them to data.police.uk.</span></div>';
            return html;
        }
        return html+crimeChangeHTML(props, opts, scale, level)+'</div>';
    }
    const value = crimeValue(details, opts);
    const total = details && details.total;

    let html = areaMapPopupCSS(isMobile)+'<div class="areaMapPopup">';
    html += '<span class="tag">Crime · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);
    if (!total || !has(total.numpeople)) {
        html += '<span class="none">No street-level crime figures are published here. Police Scotland does not publish them to data.police.uk.</span></div>';
        return html;
    }
    // A police force that skipped months (crime_coverage in load_crime.sql)
    if (total.unpublished) {
        html += '<span class="none">No crime figures here. '+escapeHTML(total.unpublished)+'</span>'+changePillsHTML(opts)+'</div>';
        return html;
    }
    if (!has(value)) {
        html += '<span class="none">No figures for '+crimeName(category)+' here.</span>';
    }
    else {
        html += '<span class="big">'+formatCrime(value, metric)+' <small>'+CRIME_METRICS[metric].name+'</small></span>';
        // A local authority's rate is marked on the scale beside its region's and country's;
        // a count is not, as theirs are far larger
        const hint = '<div class="hint"><span>Lowest '+formatCrime(scale.min, metric)+'</span><span>Highest '+formatCrime(scale.max, metric)+'</span></div>';
        if (!level.within && metric==='numcrimesper100' && (props.compare || []).length>0) {
            html += compareScaleHTML(props, scale, value, (d) => crimeValue(d, opts), (v) => formatCrime(v, metric), hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
    }

    html += pillsHTML('Show', 'metric', Object.fromEntries(Object.keys(CRIME_METRICS).map((k) => [k, CRIME_METRICS[k].pill])), metric);
    html += changePillsHTML(opts);

    // All crimes, then every crime type here, most first: one bar split by type, and a
    // row each with its rate and count; picking one maps it
    const crimeTypes = (total && total.crimetypes) || {};
    const types = Object.keys(crimeTypes).filter((k) => crimeTypes[k]>0).sort((a, b) => crimeTypes[b]-crimeTypes[a] || a.localeCompare(b));
    const shades = rampShades(heatmapSchemeColors('rust').slice().reverse(), Math.max(types.length, 1));
    const sum = types.reduce((acc, k) => acc+crimeTypes[k], 0);
    const typeRow = (k, swatch, extra='') => {
        const f = crimeFigures(details, k);
        const cls = [k===category ? 'chosen' : '', extra].filter(Boolean).join(' ');
        return '<div'+(cls ? ' class="'+cls+'"' : '')+'><i'+(swatch ? ' style="background:'+swatch+'"' : ' class="blank"')+'></i>'+
            '<span data-category="'+escapeHTML(k)+'" role="button">'+escapeHTML(CRIME_CATEGORIES[k] || k)+'</span>'+
            '<span class="val"><b>'+formatCrime(f.numcrimesper100, 'numcrimesper100')+'</b><small>'+plain.format(f.numcrimes)+' crimes'+
            // Each type's share of all the crimes
            ((k!=='total' && sum>0) ? ' · '+Math.round(100*f.numcrimes/sum)+'%' : '')+'</small></span></div>';
    };
    html += '<span class="head">Crime types · per 100 people</span>';
    if (sum>0) {
        html += '<div class="stack">'+types.map((k, i) => '<i style="width:'+(100*crimeTypes[k]/sum).toFixed(1)+'%;background:'+shades[i]+'"></i>').join('')+'</div>';
    }
    // The top 3 types, the rest behind More (open while a hidden one is mapped)
    const more = moreState(types.length, types.indexOf(category));
    html += '<div class="keys'+moreListClass(more)+'">'+typeRow('total', null)+
        types.map((k, i) => typeRow(k, shades[i], moreRowClass(i, more))).join('')+'</div>'+moreToggleHTML(types.length, more);
    html += '<div class="source">Source: data.police.uk'+(crimePeriod(total) ? ', '+crimePeriod(total) : '')+
        (has(total.numworkday) ? '. Rates use the people who live or work in the area (Census 2021 workday population).' :
            '. Rates use the number of people living in the area.')+leftOutText(props.compare)+'</div>';
    html += '</div>';
    return html;
};


// --- Deprivation within one local authority (ukmap.imd, imd_area_map.sql) ---

// Each kind of deprivation, by its key in ukmap.imd details (its rank is the same key with
// 'rank' for 'score')
const IMD_DOMAINS = {
    imdscore:'Overall', incscore:'Income', empscore:'Employment', eduscore:'Education',
    healthscore:'Health', crimescore:'Crime', housingscore:'Housing', livscore:'Living Environment',
};
const imdLadValue = (details, opts) => {
    const v = details && details.lad ? details.lad[opts.domain] : null;
    return has(v) ? Number(v) : null;
};
const ordinal = (n) => {
    const tens = n%100;
    return n+((tens>=11 && tens<=13) ? 'th' : ({1:'st', 2:'nd', 3:'rd'}[n%10] || 'th'));
};
// A neighbourhood's place among count, rank 1 being the most deprived: counted from
// whichever end it is nearer, so a low score never reads as '160th most deprived'
export const deprivationRankText = (rank, count) => {
    if (rank===1) return 'the most deprived';
    if (rank===count) return 'the least deprived';
    return rank<=count/2 ? ordinal(rank)+' most deprived' : ordinal(count-rank+1)+' least deprived';
};

const imdLsoaPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details || {};
    const lad = details.lad || {};
    const country = details.country || {};
    const {domain} = opts;
    const value = imdLadValue(details, opts);
    const ladName = lad.areaname || level.within;

    let html = areaMapPopupCSS(isMobile)+'<div class="areaMapPopup">';
    html += '<span class="tag">Deprivation · 2025</span>';
    // With the way back to the local area map it was opened from, when it was
    html += titleHTML(props.areanm, level);
    if (!has(value)) {
        html += '<span class="none">'+escapeHTML(ladName)+' has only one neighbourhood, so there is nothing to rank it against.</span></div>';
        return html;
    }
    const rank = Number(lad[domain.replace('score', 'rank')]);
    const count = Number(lad.count);
    const where = sentenceCase(deprivationRankText(rank, count));
    html += '<span class="big">'+Math.round(value)+' <small>out of 100</small></span>';
    html += '<span class="sub">'+IMD_DOMAINS[domain]+'. '+where+' of '+count+' neighbourhoods in '+escapeHTML(ladName)+'.</span>';
    html += scaleHTML(scale, value);
    html += '<div class="hint"><span>Least deprived</span><span>Most deprived</span></div>';
    if (has(country[domain])) {
        html += '<span class="rank">Across England: '+Math.round(Number(country[domain]))+' out of 100</span>';
    }
    // Every kind of deprivation, ranked within the local authority; picking one maps it
    html += '<span class="head">Within '+escapeHTML(ladName)+' · out of 100</span><div class="rows">';
    Object.keys(IMD_DOMAINS).forEach((k) => {
        const v = imdLadValue(details, {domain:k});
        if (!has(v)) return;
        const chosen = k===domain ? ' chosen' : '';
        html += '<span class="type'+chosen+'"><span data-domain="'+k+'" role="button">'+IMD_DOMAINS[k]+'</span></span>'+
            '<span class="val'+chosen+'"><b>'+Math.round(v)+'</b><small>'+deprivationRankText(Number(lad[k.replace('score', 'rank')]), count)+'</small></span>';
    });
    html += '</div>';
    html += '<div class="source">Source: Ministry of Housing, Communities &amp; Local Government, English Indices of Deprivation 2025. '+
        '100 is the local authority\'s most deprived neighbourhood, 0 its least.</div>';
    html += '</div>';
    return html;
};

// --- Deprivation by local authority (ukmap.imd_lad, imd_area_map.sql) ---

// England's LSOAs: the IoD's local authority average rank counts the most deprived as this
const IMD_LSOA_COUNT = 33755;
// A local authority's details are {score, rank, count}: for each kind of deprivation (the
// keys of IMD_DOMAINS, and the same with 'rank') its average rank, the population weighted
// average of its neighbourhoods' ranks counting the most deprived as 33,755, so higher is
// more deprived, and the rank of that among England's count (296), 1 the most deprived.
// Drilled down, the map shows its neighbourhoods from ukmap.imd, with their own details.
const imdAverageRank = (details, domain) => {
    const v = details && details.score ? details.score[domain] : null;
    return has(v) ? Number(v) : null;
};
const deprivationLadValue = (details, opts) =>
    (details && details.lad) ? imdLadValue(details, opts) : imdAverageRank(details, opts.domain);
// The average rank as 0 to 100, 100 the most deprived: the population weighted average of
// the neighbourhoods' figures across England, as the neighbourhood popups show them
const averageOutOf100 = (averageRank) => 100*(averageRank-1)/IMD_LSOA_COUNT;

// A local authority's place among count, rank 1 the most deprived, as the share of the
// others it is more (or less) deprived than: '108th least deprived' read as very deprived
const deprivationShareText = (rank, count) => {
    if (count<=1) return '';
    if (rank===1) return 'the most deprived';
    if (rank===count) return 'the least deprived';
    return rank<=(count+1)/2 ? 'more deprived than '+Math.round(100*(count-rank)/(count-1))+'%'
        : 'less deprived than '+Math.round(100*(rank-1)/(count-1))+'%';
};

const deprivationLadPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details || {};
    // A neighbourhood, once drilled down
    if (details.lad || details.country) return imdLsoaPopupHTML(props, opts, scale, isMobile, values, level);
    const ranks = details.rank || {};
    const count = Number(details.count);
    const {domain} = opts;
    const value = imdAverageRank(details, domain);

    let html = areaMapPopupCSS(isMobile)+'<div class="areaMapPopup">';
    html += '<span class="tag">Deprivation · 2025 · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);
    if (!has(value)) {
        html += '<span class="none">No deprivation figures here. The Indices of Deprivation cover England only.</span></div>';
        return html;
    }
    const rank = Number(ranks[domain.replace('score', 'rank')]);
    html += '<span class="big">'+ordinal(rank)+' <small>of '+count+'</small></span>';
    html += '<span class="sub">'+IMD_DOMAINS[domain]+'. '+sentenceCase(deprivationShareText(rank, count))+
        ' of local authorities in England.</span>';
    html += scaleHTML(scale, value);
    html += '<div class="hint"><span>Least deprived</span><span>Most deprived</span></div>';
    html += '<span class="rank" title="The population weighted average of its neighbourhoods\' figures, where 100 is England\'s most deprived neighbourhood">'+
        'Average neighbourhood: '+Math.round(averageOutOf100(value))+' out of 100 across England</span>';
    // Every kind of deprivation, ranked among the local authorities; picking one maps it
    html += '<span class="head">Rank of '+count+' local authorities · 1st most deprived</span><div class="rows">';
    Object.keys(IMD_DOMAINS).forEach((k) => {
        const v = imdAverageRank(details, k);
        const r = Number(ranks[k.replace('score', 'rank')]);
        if (!has(v) || !has(r)) return;
        const chosen = k===domain ? ' chosen' : '';
        html += '<span class="type'+chosen+'"><span data-domain="'+k+'" role="button">'+IMD_DOMAINS[k]+'</span></span>'+
            '<span class="val'+chosen+'"><b>'+ordinal(r)+'</b><small>'+deprivationShareText(r, count)+'</small></span>';
    });
    html += '</div>';
    html += '<div class="source">Source: Ministry of Housing, Communities &amp; Local Government, English Indices of Deprivation 2025, '+
        'local authority summaries. Ranked by the average rank of each local authority\'s neighbourhoods, weighted by population; 1st is the most deprived.</div>';
    html += '</div>';
    return html;
};

// --- Private rents by local authority (ukmap.rent_area, rent_area_map.sql) ---

// The kinds of home the map can show, by their key in the details json: all homes, by
// number of bedrooms, then by type of home
const RENT_TYPES = {
    all:{data:'Overall', pill:'All homes', name:'all homes'},
    b1:{data:'1 bed', pill:'1 bed', name:'1 bedroom homes'},
    b2:{data:'2 bed', pill:'2 bed', name:'2 bedroom homes'},
    b3:{data:'3 bed', pill:'3 bed', name:'3 bedroom homes'},
    b4:{data:'4+ bed', pill:'4+ bed', name:'4 or more bedroom homes'},
    D:{data:'Detached', pill:'Detached', name:'detached houses'},
    S:{data:'Semi-Detached', pill:'Semi', name:'semi-detached houses'},
    T:{data:'Terraced', pill:'Terraced', name:'terraced houses'},
    F:{data:'Flat/Maisonette', pill:'Flats', name:'flats and maisonettes'},
};
const RENT_SIZES = ['b1', 'b2', 'b3', 'b4'];
const RENT_KINDS = ['D', 'S', 'T', 'F'];
const RENT_TYPE_PILLS = Object.fromEntries(Object.keys(RENT_TYPES).map((k) => [k, RENT_TYPES[k].pill]));

// The latest monthly rent for the chosen kind of home, or in a change view its % change
const rentValue = (details, opts) => {
    if (isViewChange(opts)) return periodPct(periodFor(details, opts), RENT_TYPES[opts.type].data);
    const v = details && details.latest ? details.latest[RENT_TYPES[opts.type].data] : null;
    return has(v) ? Number(v) : null;
};
const formatRent = (v) => money.format(v);
const formatRentFigure = (v, opts) => isViewChange(opts) ? formatPct(v) : formatRent(v);
// When a change view's rents are from: the earlier month ('since Aug 2021')
const rentSince = (details, opts) => {
    const period = periodFor(details, opts);
    return period && period.maxdate ? 'since '+formatMonYear(period.maxdate) : '';
};

// A rent change view's popup body: the change in the chosen kind of home's rent, against
// the region and country, then every kind of home's change
const rentChangeHTML = (props, opts, scale, values, wider) => {
    const details = props.details || {};
    const {type} = opts;
    const typeName = RENT_TYPES[type].name;
    const period = periodFor(details, opts);
    const value = rentValue(details, opts);
    let html = '';
    if (!period) {
        html += '<span class="none">ONS has no rents here from that far back to compare with.</span>';
    }
    else if (!has(value)) {
        html += '<span class="none">No change for '+typeName+' here. Try another kind of home.</span>';
    }
    else {
        const from = formatMonYear(period.maxdate);
        const to = formatMonYear(details.change.maxdate || details.maxdate);
        html += '<span class="big '+riseClass(value)+'">'+formatPct(value)+' <small>since '+from+'</small></span>';
        html += '<span class="sub">Average private rent, '+typeName+', '+from+' to '+to+'</span>';
        const hint = '<div class="hint"><span>Fell most '+formatPct(scale.min)+'</span><span>Rose most '+formatPct(scale.max)+'</span></div>';
        if (wider.length>0) {
            html += compareScaleHTML(props, scale, value, (d) => rentValue(d, opts), formatPct, hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
        const ranked = values.filter(has);
        if (ranked.length>1) {
            const pos = ranked.filter((v) => v>value).length+1;
            html += '<span class="rank">'+(pos<=ranked.length/2 ? ordinal(pos)+' biggest rise' : ordinal(ranked.length-pos+1)+' smallest rise')+
                ' of '+ranked.length+' local authorities</span>';
        }
    }
    return html;
};

const rentLadPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details || {};
    const {type} = opts;
    const value = rentValue(details, opts);
    const typeName = RENT_TYPES[type].name;
    // Its region (England only) and country, for the rows' comparison
    const wider = (props.compare || []).map((c) => ({name:c.name, details:c.details || {}})).filter((c) => c.details.latest);

    const isChange = isViewChange(opts);
    const fmt = (v) => formatRentFigure(v, opts);

    let html = areaMapPopupCSS(isMobile)+
        '<style>.areaMapPopup .rows [data-type] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
        '.areaMapPopup .rows [data-type]:hover {text-decoration-color:'+BRAND.ink+';}</style>'+
        '<div class="areaMapPopup orange">';
    html += '<span class="tag">Private rents'+(isChange ? ' change' : '')+' · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);

    // No figures of its own: Scotland's council areas, the City of London, the Isles of Scilly.
    // The larger area it is in stands in, where ONS has one
    if (!details.latest) {
        const scotland = String(props.areacd || '').charAt(0)==='S';
        html += '<span class="none">'+(scotland ? 'ONS publishes private rents for Scotland as a whole, not for each council area.'
            : 'ONS publishes no private rents for '+escapeHTML(props.areanm)+', as too few homes are let here.')+'</span>';
        if (wider.length>0) {
            const c = wider[0];
            const v = rentValue(c.details, opts);
            html += '<span class="head">'+escapeHTML(c.name)+' · '+typeName+(c.details.maxdate ? ' · '+formatMonYear(c.details.maxdate) : '')+'</span>';
            html += !has(v) ? '<span class="none">No figure for '+typeName+'.</span>' : isChange ?
                '<span class="big '+riseClass(v)+'">'+formatPct(v)+' <small>'+rentSince(c.details, opts)+'</small></span>' :
                '<span class="big">'+formatRent(v)+' <small>a month</small></span>';
        }
        html += pillsHTML('Home', 'type', RENT_TYPE_PILLS, type);
        html += changePillsHTML(opts);
        html += '<div class="source">Source: Office for National Statistics, Price Index of Private Rents.</div></div>';
        return html;
    }

    const month = formatMonYear(details.maxdate);
    if (isChange) {
        html += rentChangeHTML(props, opts, scale, values, wider);
        html += changePillsHTML(opts);
    }
    else if (!has(value)) {
        html += '<span class="none">No figure for '+typeName+' here. Try another kind of home.</span>';
    }
    else {
        html += '<span class="big">'+formatRent(value)+' <small>a month</small></span>';
        html += '<span class="sub">Average private rent, '+typeName+', '+month+'</span>';
        const hint = '<div class="hint"><span>Lowest '+formatRent(scale.min)+'</span><span>Highest '+formatRent(scale.max)+'</span></div>';
        // Marked on the scale beside its region and country
        if (wider.length>0) {
            html += compareScaleHTML(props, scale, value, (d) => rentValue(d, opts), formatRent, hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
        // Where it stands among every local authority with a figure
        const ranked = values.filter(has);
        if (ranked.length>1) {
            const pos = ranked.filter((v) => v>value).length+1;
            html += '<span class="rank">'+(pos<=ranked.length/2 ? ordinal(pos)+' most expensive' : ordinal(ranked.length-pos+1)+' cheapest')+
                ' of '+ranked.length+' local authorities</span>';
        }
        html += changePillsHTML(opts);
    }

    // Every kind of home here, with its region's (or country's) rent under it; picking one maps it
    const near = wider[0];
    const row = (k) => {
        const rent = rentValue(details, {...opts, type:k});
        if (!has(rent)) return '';
        const nearRent = near ? rentValue(near.details, {...opts, type:k}) : null;
        const chosen = k===type ? ' chosen' : '';
        return '<span class="type'+chosen+'"><span data-type="'+k+'" role="button">'+RENT_TYPES[k].pill+'</span></span>'+
            '<span class="val'+chosen+'"><b'+(isChange ? ' class="'+riseClass(rent)+'"' : '')+'>'+fmt(rent)+'</b>'+
            (has(nearRent) ? '<small>'+nameWithFigure(near.name, fmt(nearRent))+'</small>' : '')+'</span>';
    };
    html += '<span class="head">By home · '+(isChange ? 'change '+rentSince(details, opts) : 'a month')+'</span><div class="rows">'+row('all')+'</div>';
    html += '<span class="head">By bedrooms</span><div class="rows">'+RENT_SIZES.map(row).join('')+'</div>';
    html += '<span class="head">By type of home</span><div class="rows">'+RENT_KINDS.map(row).join('')+'</div>';

    html += '<div class="source">Source: Office for National Statistics, Price Index of Private Rents, '+month+
        '. The average monthly rent paid by private tenants, new and existing lets'+
        (isChange ? ', against the same month '+changeSpanText(opts)+' earlier' : '')+'.</div>';
    html += '</div>';
    return html;
};

// --- Sales per 100 people by local authority (ukmap.sales_rate_area, sales_rate_area_map.sql) ---

// The kinds of home the map can show, by their key in the details json's types
const SALES_RATE_TYPES = {
    all:{pill:'All homes', name:'all homes'},
    F:{pill:'Flats', name:'flats'},
    houses:{pill:'Houses', name:'houses'},
    D:{pill:'Detached', name:'detached houses'},
    S:{pill:'Semi', name:'semi-detached houses'},
    T:{pill:'Terraced', name:'terraced houses'},
};
const SALES_RATE_PILLS = Object.fromEntries(Object.keys(SALES_RATE_TYPES).map((k) => [k, SALES_RATE_TYPES[k].pill]));

// Sales in the 12 months for every 100 people living there, for the chosen kind of home,
// or in a change view the % change in its sales (the same as in the rate, as the population
// is the same estimate for both)
const salesRateValue = (details, opts) => {
    if (isViewChange(opts)) return periodPct(periodFor(details, opts), opts.type);
    const t = details && details.types ? details.types[opts.type] : null;
    return t && has(t.per100) ? Number(t.per100) : null;
};
const formatSalesRate = (v) => (Math.round(v*100)/100).toFixed(2);
const formatSalesRateFigure = (v, opts) => isViewChange(opts) ? formatPct(v) : formatSalesRate(v);
const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? (dateStr || '') : d.toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric', timeZone:'UTC'});
};
// The 12 months, and the population estimate's year
export const salesRatePeriod = (details) => (details && details.mindate && details.maxdate) ?
    formatDay(details.mindate)+' to '+formatDay(details.maxdate) : '';
// When a change view's earlier 12 months end ('since May 2021')
const salesRateSince = (details, opts) => {
    const period = periodFor(details, opts);
    return period && period.maxdate ? 'since '+formatMonYear(period.maxdate) : '';
};

// A sales rate change view's popup body: the change in the chosen kind of home's sales,
// against the region and country (a neighbourhood's: its local authority and country)
const salesRateChangeHTML = (props, opts, scale, values, level) => {
    const details = props.details || {};
    const typeName = SALES_RATE_TYPES[opts.type].name;
    const change = details.change || {};
    const period = periodFor(details, opts);
    const value = salesRateValue(details, opts);
    if (!period) return '<span class="none">No change figures here.</span>';
    if (!has(value)) return '<span class="none">Too few sales of '+typeName+' here to compare. Try another kind of home.</span>';
    const span = (p) => formatMonYear(p.mindate)+' to '+formatMonYear(p.maxdate);
    let html = '<span class="big '+riseClass(value)+'">'+formatPct(value)+' <small>'+salesRateSince(details, opts)+'</small></span>';
    html += '<span class="sub">'+sentenceCase(typeName)+' sold, '+span(change)+' against '+span(period)+'</span>';
    const hint = '<div class="hint"><span>Fell most '+formatPct(scale.min)+'</span><span>Rose most '+formatPct(scale.max)+'</span></div>';
    if ((props.compare || []).length>0) {
        html += compareScaleHTML(props, scale, value, (d) => salesRateValue(d, opts), formatPct, hint);
    }
    else {
        html += scaleHTML(scale, value)+hint;
    }
    if (has(change.num) && has(period.num)) {
        html += '<span class="rank">'+plain.format(period.num)+' homes sold then, '+plain.format(change.num)+' now</span>';
    }
    const ranked = values.filter(has);
    if (ranked.length>1) {
        const pos = ranked.filter((v) => v>value).length+1;
        html += '<span class="rank">'+(pos<=ranked.length/2 ? ordinal(pos)+' biggest rise' : ordinal(ranked.length-pos+1)+' biggest fall')+
            ' of '+ranked.length+' '+level.areaNamePlural+(level.within ? ' in '+escapeHTML(level.within) : '')+'</span>';
    }
    return html;
};

const salesRateLadPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details || {};
    const {type} = opts;
    const value = salesRateValue(details, opts);
    const typeName = SALES_RATE_TYPES[type].name;
    const period = salesRatePeriod(details);
    const fig = (details.types || {})[type] || {};
    const isChange = isViewChange(opts);
    const fmt = (v) => formatSalesRateFigure(v, opts);

    let html = areaMapPopupCSS(isMobile)+
        '<style>.areaMapPopup .rows [data-type] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
        '.areaMapPopup .rows [data-type]:hover {text-decoration-color:'+BRAND.ink+';}</style>'+
        '<div class="areaMapPopup orange">';
    html += '<span class="tag">Sales per 100 people'+(isChange ? ' · change' : '')+' · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);

    if (isChange) {
        html += salesRateChangeHTML(props, opts, scale, values, level);
    }
    else if (!has(value)) {
        html += '<span class="none">No figure for '+typeName+' here. Try another kind of home.</span>';
    }
    else {
        html += '<span class="big">'+formatSalesRate(value)+' <small>sales per 100 people</small></span>';
        html += '<span class="sub">'+sentenceCase(typeName)+': '+plain.format(fig.numsales)+' sales, '+
            plain.format(details.numpeople)+' people</span>';
        const hint = '<div class="hint"><span>Lowest '+formatSalesRate(scale.min)+'</span><span>Highest '+formatSalesRate(scale.max)+'</span></div>';
        // Marked on the scale beside its region and country (a neighbourhood's: its local authority and country)
        if ((props.compare || []).length>0) {
            html += compareScaleHTML(props, scale, value, (d) => salesRateValue(d, opts), formatSalesRate, hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
        // Where it stands among every area shown: the local authorities, or the neighbourhoods in one
        const ranked = values.filter(has);
        if (ranked.length>1) {
            const pos = ranked.filter((v) => v>value).length+1;
            html += '<span class="rank">'+(pos<=ranked.length/2 ? ordinal(pos)+' highest' : ordinal(ranked.length-pos+1)+' lowest')+
                ' of '+ranked.length+' '+level.areaNamePlural+(level.within ? ' in '+escapeHTML(level.within) : '')+'</span>';
        }
    }

    html += pillsHTML('Home', 'type', SALES_RATE_PILLS, type);
    html += changePillsHTML(opts);

    // Every kind of home here, with its sales (or their change) under it; picking one maps it
    const near = (props.compare || [])[0];
    const rows = Object.keys(SALES_RATE_TYPES).map((k) => {
        const rate = salesRateValue(details, {...opts, type:k});
        if (!has(rate)) return '';
        const t = (details.types || {})[k] || {};
        const nearRate = near ? salesRateValue(near.details, {...opts, type:k}) : null;
        const chosen = k===type ? ' chosen' : '';
        return '<span class="type'+chosen+'"><span data-type="'+k+'" role="button">'+SALES_RATE_TYPES[k].pill+'</span></span>'+
            '<span class="val'+chosen+'"><b'+(isChange ? ' class="'+riseClass(rate)+'"' : '')+'>'+fmt(rate)+'</b>'+
            (!isChange && has(t.numsales) ? '<small>'+plain.format(t.numsales)+' sales</small>' : '')+
            (has(nearRate) ? '<small>'+nameWithFigure(near.name, fmt(nearRate))+'</small>' : '')+'</span>';
    }).join('');
    html += '<span class="head">By type of home · '+(isChange ? 'change '+salesRateSince(details, opts) : 'per 100 people')+'</span><div class="rows">'+rows+'</div>';

    html += '<div class="source">Sales: HM Land Registry Price Paid Data'+(period ? ', '+period : '')+
        ', standard sales only. People: ONS small area population estimates, mid-'+escapeHTML(details.popyear || '')+
        '. Sales take a few months to be registered, so the latest months are not all in yet'+
        (isChange ? '; changes compare 12 months ending three months before the latest sale, against the same months '+
            changeSpanText(opts)+' earlier, so they are not affected. Types with under 20 sales then are not compared' : '')+'.</div>';
    html += '</div>';
    return html;
};

// --- Moving in and out by local authority (ukmap.migration_area, migration.sql) ---

// The age groups, by their key in the details json's ages
export const MIGRATION_AGES = {all:'All', '0-14':'0–14', '15-24':'15–24', '25-44':'25–44', '45-64':'45–64', '65+':'65+'};
const MIGRATION_AGE_NAMES = {'0-14':'children (0–14)', '15-24':'15–24s', '25-44':'25–44s', '45-64':'45–64s', '65+':'over-65s'};
const MIGRATION_AGE_KEYS = Object.keys(MIGRATION_AGES).filter((k) => k!=='all');
// Gains in green and losses in purple, palest first
export const MIGRATION_GAIN = ['#dbefd3', '#a9d49b', '#5fae63', '#1f7d3c', '#0b4d22'];
export const MIGRATION_LOSS = ['#ece0ef', '#cbb2d8', '#9c7ab3', '#6e3d8a', '#43165c'];
const GAIN_TEXT = MIGRATION_GAIN[3];
const LOSS_TEXT = MIGRATION_LOSS[3];
// The shares of areas (by size of their net figure, gain or loss) the band edges fall at
const MIGRATION_BAND_QUANTILES = [0.3, 0.55, 0.78, 0.93];

// A round number near v (two significant figures), for band edges that read well in the key
export const niceRound = (v) => {
    if (!(v>0)) return 1;
    const p = Math.pow(10, Math.floor(Math.log10(v))-1);
    return Math.max(1, Math.round(v/p)*p);
};

// The net moves' colours: gains and losses banded alike by their size, so a gain and a loss
// of the same size are the same depth of green and purple. The strip runs from the deepest
// purple to the deepest green; edges are the band edges for the size of the figure.
export const migrationScale = (values) => {
    const vals = values.filter(has).map(Number);
    if (vals.length===0) return null;
    const sizes = vals.map((v) => Math.abs(v)).sort((a, b) => a-b);
    const edges = MIGRATION_BAND_QUANTILES.map((q) => niceRound(sizes[Math.min(sizes.length-1, Math.floor(sizes.length*q))]));
    const colors = MIGRATION_LOSS.slice().reverse().concat(MIGRATION_GAIN);
    const bandOf = (v) => {
        const i = bandFor(Math.abs(v), edges);
        return v>=0 ? MIGRATION_LOSS.length+i : MIGRATION_LOSS.length-1-i;
    };
    return {colors, min:Math.min(...vals), max:Math.max(...vals), edges, bandOf, colorOf:(v) => colors[bandOf(v)]};
};

const formatSigned = (v) => (v>0 ? '+' : v<0 ? '−' : '')+plain.format(Math.round(Math.abs(v)));
const migrationColor = (v) => v>=0 ? GAIN_TEXT : LOSS_TEXT;
const migrationAge = (details, age) => (details && details.ages && details.ages[age]) || null;
// The net moves for the chosen age group (all ages, or one group)
const migrationValue = (details, opts) => {
    if (!details) return null;
    if (opts.age && opts.age!=='all') {
        const a = migrationAge(details, opts.age);
        return a && has(a.net) ? Number(a.net) : null;
    }
    return has(details.net) ? Number(details.net) : null;
};
const migrationPeriod = (details) => (details && details.period) || '';

// The places an area's arrows go to and come from, for the arrows mode: 'net', the 3 it gains
// most people from and the 3 it loses most to, after moves both ways cancel out; 'all', the
// 3 most people came from and the 3 most went to. Each {cd, nm, x, y, value, dir}.
export const migrationFlows = (details, opts) => {
    if (!details) return [];
    const pick = (list, dir, key, sign) => (list || []).filter((p) => has(p.x) && has(p.y) && has(p[key]))
        .map((p) => ({cd:p.cd, nm:p.nm, x:Number(p.x), y:Number(p.y), value:sign*Number(p[key]), dir}));
    if (opts.arrows==='all') {
        const g = details.gross || {};
        return pick(g.in, 'in', 'in', 1).concat(pick(g.out, 'out', 'out', 1));
    }
    const n = details.netflows || {};
    return pick(n.gains, 'in', 'net', 1).concat(pick(n.losses, 'out', 'net', -1));
};

// One sentence on what happened: who came and went, and which ages it gained or lost most
export const migrationSummary = (name, details) => {
    const n = escapeHTML(name);
    const net = Number(details.net), inflow = plain.format(Math.round(details.inflow)), outflow = plain.format(Math.round(details.outflow));
    let s = net<0 ? 'More people left '+n+' than arrived: '+outflow+' out and '+inflow+' in.'
        : 'More people arrived in '+n+' than left: '+inflow+' in and '+outflow+' out.';
    const ages = MIGRATION_AGE_KEYS.map((k) => ({k, v:(migrationAge(details, k) || {}).net})).filter((a) => has(a.v));
    if (ages.length===0) return s;
    const hi = ages.reduce((a, b) => b.v>a.v ? b : a), lo = ages.reduce((a, b) => b.v<a.v ? b : a);
    if (hi.v>0 && lo.v<0) s += ' It gained '+MIGRATION_AGE_NAMES[hi.k]+' ('+formatSigned(hi.v)+') but lost '+MIGRATION_AGE_NAMES[lo.k]+' ('+formatSigned(lo.v)+').';
    else if (lo.v>=0) s += ' It gained people in every age group, most of all '+MIGRATION_AGE_NAMES[hi.k]+' ('+formatSigned(hi.v)+').';
    else s += ' It lost people in every age group, most of all '+MIGRATION_AGE_NAMES[lo.k]+' ('+formatSigned(lo.v)+').';
    return s;
};

const migrationCSS = '<style>'+
    '.areaMapPopup.mig .big {font-size:2em;} '+
    '.areaMapPopup.mig .io {display:grid; grid-template-columns:1fr 1fr; gap:6px;} '+
    '.areaMapPopup.mig .io div {border:1px solid '+BRAND.line+'; border-radius:8px; padding:5px 8px;} '+
    '.areaMapPopup.mig .io b {display:block; font-family:'+DISPLAY_FONT+'; font-size:1.05em; font-variant-numeric:tabular-nums;} '+
    '.areaMapPopup.mig .io span {font-size:.7em; color:'+BRAND.mute+';} '+
    '.areaMapPopup.mig .summary {font-size:.82em; line-height:1.45;} '+
    '.areaMapPopup.mig .places {display:grid; gap:4px; margin:0; padding:0; list-style:none;} '+
    '.areaMapPopup.mig .places li {display:grid; grid-template-columns:minmax(0, 1fr) auto; gap:2px 10px; align-items:center; font-size:.8em; padding:2px 4px; border-radius:6px;} '+
    '.areaMapPopup.mig .places li[data-select] {cursor:pointer;} '+
    '.areaMapPopup.mig .places li[data-select]:hover {background:'+BRAND.tint+';} '+
    '.areaMapPopup.mig .places li span {overflow:hidden; text-overflow:ellipsis; white-space:nowrap;} '+
    '.areaMapPopup.mig .places li b {font-variant-numeric:tabular-nums; font-weight:600;} '+
    '.areaMapPopup.mig .places li i {grid-column:1/-1; height:5px; border-radius:3px; display:block;} '+
    '.areaMapPopup.mig .bf {display:grid; grid-template-columns:44px 1fr 1fr 52px; gap:4px 6px; align-items:center; font-size:.74em; font-variant-numeric:tabular-nums;} '+
    '.areaMapPopup.mig .bf .hd {font-size:.9em; color:'+BRAND.mute+';} '+
    '.areaMapPopup.mig .bf .l {display:flex; justify-content:flex-end;} '+
    '.areaMapPopup.mig .bf .l i, .areaMapPopup.mig .bf .r i {display:block; height:11px; border-radius:2px;} '+
    '.areaMapPopup.mig .bf .n {text-align:right; font-weight:600;} '+
    '.areaMapPopup.mig .bf .on {font-weight:700;} '+
    '.areaMapPopup.mig .bf [data-age] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
    '.areaMapPopup.mig .allages {font-size:.72em; color:'+BRAND.mute+';} '+
    '</style>';

// A list of places, each with its figure and a bar against the largest; places in England
// and Wales can be picked (data-select), Scotland and Northern Ireland can't
const placeListHTML = (list, dir, color, signed) => {
    if (list.length===0) return '<span class="none">None</span>';
    const max = Math.max(1, ...list.map((x) => Math.abs(x.value)));
    return '<ul class="places">'+list.map((x) => '<li'+(/^[EW]/.test(x.cd || '') ? ' data-select="'+escapeHTML(x.cd)+'" role="button" title="See '+escapeHTML(x.nm)+'"' : '')+'>'+
        '<span>'+escapeHTML(x.nm)+'</span><b>'+(signed ? (dir==='in' ? '+' : '−') : '')+plain.format(Math.round(Math.abs(x.value)))+'</b>'+
        '<i style="width:'+(100*Math.abs(x.value)/max).toFixed(1)+'%;background:'+color+'"></i></li>').join('')+'</ul>';
};

// Who moved in and out by age: out to the left in purple, in to the right in green, the
// chosen group bold; picking a group maps it
const butterflyHTML = (details, age) => {
    const rows = MIGRATION_AGE_KEYS.map((k) => ({k, a:migrationAge(details, k)})).filter((r) => r.a);
    const max = Math.max(1, ...rows.map((r) => Math.max(r.a.in, r.a.out)));
    return '<div class="bf"><span class="hd">Age</span><span class="hd" style="text-align:right">Moved out</span><span class="hd">Moved in</span><span class="hd" style="text-align:right">Net</span>'+
        rows.map(({k, a}) => {
            const on = age==='all' || age===k;
            return '<span class="'+(age===k ? 'on' : '')+'"><span data-age="'+k+'" role="button" title="Map '+MIGRATION_AGES[k]+' year olds">'+MIGRATION_AGES[k]+'</span></span>'+
                '<span class="l" title="'+plain.format(a.out)+' moved out"><i style="width:'+(100*a.out/max).toFixed(1)+'%;background:'+MIGRATION_LOSS[on ? 3 : 1]+'"></i></span>'+
                '<span class="r" title="'+plain.format(a.in)+' moved in"><i style="width:'+(100*a.in/max).toFixed(1)+'%;background:'+MIGRATION_GAIN[on ? 3 : 1]+'"></i></span>'+
                '<span class="n" style="color:'+migrationColor(a.in-a.out)+'">'+formatSigned(a.in-a.out)+'</span>';
        }).join('')+'</div>';
};

const migrationSource = (details) => '<div class="source">Source: Office for National Statistics, Internal migration in England and Wales, '+
    escapeHTML(migrationPeriod(details))+'. Moves between local authorities in the UK, on the 2023 boundaries; '+
    'Scotland and Northern Ireland count as whole countries. Moves within a local authority and to or from abroad are not counted.</div>';

const migrationLadPopupHTML = (props, opts, scale, isMobile, values) => {
    const details = props.details || {};
    const {age, arrows} = opts;
    const net = arrows!=='all';
    const value = migrationValue(details, opts);
    let html = areaMapPopupCSS(isMobile)+migrationCSS+'<div class="areaMapPopup mig">';
    html += '<span class="tag">Moving in and out · local authority</span>';
    html += titleHTML(props.areanm, null);
    if (!has(value)) {
        return html+'<span class="none">No figures here.</span>'+migrationSource(details)+'</div>';
    }
    const a = age!=='all' ? migrationAge(details, age) : null;
    html += '<span class="big" style="color:'+migrationColor(value)+'">'+formatSigned(value)+' <small>'+
        (a ? MIGRATION_AGES[age]+' year olds, net' : 'people, net')+'</small></span>';
    html += '<div class="io"><div><b>'+plain.format(Math.round(a ? a.in : details.inflow))+'</b><span>moved in</span></div>'+
        '<div><b>'+plain.format(Math.round(a ? a.out : details.outflow))+'</b><span>moved out</span></div></div>';
    if (!a) html += '<span class="summary">'+migrationSummary(props.areanm, details)+'</span>';
    // Where it stands among every local authority
    const ranked = values.filter(has);
    if (ranked.length>1) {
        const gains = ranked.filter((v) => v>value).length+1;
        const losses = ranked.filter((v) => v<value).length+1;
        const who = a ? MIGRATION_AGES[age]+' year olds' : 'people';
        html += '<span class="rank">'+(value>=0 ? ordinal(gains)+' biggest gain' : ordinal(losses)+' biggest loss')+' of '+who+
            ' among '+ranked.length+' local authorities</span>';
    }
    html += pillsHTML('Arrows', 'arrows', {net:'Net moves', all:'All moves'}, net ? 'net' : 'all');
    const flows = migrationFlows(details, opts);
    const allAges = a ? ' <span class="allages">(all ages)</span>' : '';
    html += '<span class="head">'+(net ? 'Gains most from' : 'Came from')+allAges+'</span>'+
        placeListHTML(flows.filter((f) => f.dir==='in'), 'in', '#f5b51f', net);
    html += '<span class="head">'+(net ? 'Loses most to' : 'Went to')+allAges+'</span>'+
        placeListHTML(flows.filter((f) => f.dir==='out'), 'out', '#2ec9ea', net);
    if (a) html += '<span class="allages">The arrows and these lists are for all ages: ONS doesn\'t split the moves between places by age.</span>';
    html += '<span class="head">Who moved, by age</span>'+butterflyHTML(details, age);
    html += migrationSource(details)+'</div>';
    return html;
};

// With no local authority picked: England and Wales's biggest gains and losses for the age
// group, any of which can be picked
export const migrationNationalHTML = (features, opts, isMobile) => {
    const rows = features.map((f) => ({cd:f.areacd, nm:f.areanm, value:migrationValue(f.details, opts)})).filter((r) => has(r.value))
        .sort((x, y) => y.value-x.value);
    const who = opts.age!=='all' ? MIGRATION_AGES[opts.age]+' year olds' : 'people';
    const sample = (features[0] || {}).details;
    let html = areaMapPopupCSS(isMobile)+migrationCSS+'<div class="areaMapPopup mig">';
    html += '<span class="tag">Moving in and out · '+escapeHTML(migrationPeriod(sample))+'</span>';
    html += '<div class="title"><span class="name">England and Wales</span></div>';
    html += '<span class="summary">'+(isMobile ? 'Tap' : 'Click')+' a local authority to see where it gains '+who+' from and loses them to.</span>';
    html += '<span class="head">Gained most '+who+'</span>'+placeListHTML(rows.slice(0, 5).filter((r) => r.value>0), 'in', GAIN_TEXT, true);
    html += '<span class="head">Lost most '+who+'</span>'+placeListHTML(rows.slice(-5).reverse().filter((r) => r.value<0), 'out', LOSS_TEXT, true);
    html += migrationSource(sample)+'</div>';
    return html;
};

// --- Broadband and mobile coverage by local authority (ukmap.connectivity_area, connectivity.sql) ---

// What the map can show: a broadband key (share of premises, or a speed) or a mobile measure
// (share of premises, by network). Mobile is published by local authority only
export const CONNECTIVITY_METRICS = {
    gigabit: {pill:'Gigabit', key:'gigabit', name:'can get gigabit broadband'},
    ufbb: {pill:'Ultrafast', key:'ufbb', name:'can get ultrafast broadband (300 Mbit/s+)'},
    sfbb: {pill:'Superfast', key:'sfbb', name:'can get superfast broadband (30 Mbit/s+)'},
    under10: {pill:'Under 10 Mbit/s', key:'under10', name:'can\'t get 10 Mbit/s broadband'},
    speed: {pill:'Speed', key:'down_med', name:'median download speed', speed:true},
    mob4g: {pill:'4G indoors', measure:'4g_in', name:'have 4G indoors', mobile:true},
    mob5g: {pill:'5G outdoors', measure:'5g_out', name:'have 5G outdoors', mobile:true},
};
const CONNECTIVITY_PILLS = Object.fromEntries(Object.entries(CONNECTIVITY_METRICS).map(([k, m]) => [k, m.pill]));
const CONNECTIVITY_NETWORKS = {all:'All four', ...Object.fromEntries(NETWORKS.map((n) => [n, n]))};

const num = (v) => (v===null || v===undefined || v==='' || isNaN(Number(v))) ? null : Number(v);

// One network's mobile figures; 'all' is Ofcom's "all four networks" column
const mobileOf = (details, network) => {
    const m = (details || {}).mobile;
    if (!m) return null;
    return network==='all' ? (m.n4 || m.all || null) : (m[network] || null);
};

export const connectivityValue = (details, opts) => {
    const m = CONNECTIVITY_METRICS[opts.metric] || CONNECTIVITY_METRICS.gigabit;
    if (m.mobile) {
        const n = mobileOf(details, opts.network);
        return n ? num(n[m.measure]) : null;
    }
    return num(((details || {}).broadband || {})[m.key]);
};

export const formatConnectivity = (v, opts) => {
    if (!has(v)) return '–';
    const m = CONNECTIVITY_METRICS[opts && opts.metric] || CONNECTIVITY_METRICS.gigabit;
    return m.speed ? Math.round(v)+' Mbit/s' : coverageText(v);
};

const connectivityWhat = (opts) => {
    const m = CONNECTIVITY_METRICS[opts.metric] || CONNECTIVITY_METRICS.gigabit;
    if (m.speed) return 'Median download speed';
    return 'Premises that '+m.name+(m.mobile ? ' ('+(opts.network==='all' ? 'all four networks' : opts.network)+')' : '');
};

const connectivityPopupHTML = (props, opts, scale, isMobile, values, level) => {
    const details = props.details || {};
    const m = CONNECTIVITY_METRICS[opts.metric] || CONNECTIVITY_METRICS.gigabit;
    const value = connectivityValue(details, opts);
    const fmt = (v, o=opts) => formatConnectivity(v, o);

    let html = areaMapPopupCSS(isMobile)+
        '<style>.areaMapPopup .rows [data-metric], .areaMapPopup .rows [data-network] {cursor:pointer; text-decoration:underline; text-decoration-color:#b9c4bf; text-underline-offset:2px;} '+
        '.areaMapPopup .rows [data-metric]:hover, .areaMapPopup .rows [data-network]:hover {text-decoration-color:'+BRAND.ink+';}</style>'+
        '<div class="areaMapPopup">';
    html += '<span class="tag">Broadband and mobile · '+level.areaName+'</span>';
    html += titleHTML(props.areanm, level);

    if (m.mobile && !details.mobile) {
        html += '<span class="none">Ofcom publishes mobile coverage by local authority only. Pick a broadband figure to see '+
            level.areaNamePlural+'.</span>';
    }
    else if (!has(value)) {
        html += '<span class="none">No figure for this here.</span>';
    }
    else {
        html += '<span class="big">'+fmt(value)+' <small>'+(m.speed ? 'median download' : 'of premises')+'</small></span>';
        html += '<span class="sub">'+escapeHTML(connectivityWhat(opts))+'</span>';
        const hint = '<div class="hint"><span>Lowest '+fmt(scale.min)+'</span><span>Highest '+fmt(scale.max)+'</span></div>';
        if ((props.compare || []).length>0) {
            html += compareScaleHTML(props, scale, value, (d) => connectivityValue(d, opts), fmt, hint);
        }
        else {
            html += scaleHTML(scale, value)+hint;
        }
        const ranked = values.filter(has);
        if (ranked.length>1) {
            const pos = ranked.filter((v) => v>value).length+1;
            html += '<span class="rank">'+(pos<=ranked.length/2 ? ordinal(pos)+' highest' : ordinal(ranked.length-pos+1)+' lowest')+
                ' of '+ranked.length+' '+level.areaNamePlural+(level.within ? ' in '+escapeHTML(level.within) : '')+'</span>';
        }
    }

    html += pillsHTML('Show', 'metric', CONNECTIVITY_PILLS, opts.metric);
    if (m.mobile) html += pillsHTML('Network', 'network', CONNECTIVITY_NETWORKS, opts.network);

    // Every broadband figure, then each network's 4G indoors and 5G outdoors; picking one maps it
    const near = (props.compare || [])[0];
    const row = (label, attr, chosen, v, o) => {
        const nearV = near ? connectivityValue(near.details, o) : null;
        return '<span class="type'+(chosen ? ' chosen' : '')+'"><span '+attr+' role="button">'+label+'</span></span>'+
            '<span class="val'+(chosen ? ' chosen' : '')+'"><b>'+fmt(v, o)+'</b>'+
            (has(nearV) ? '<small>'+nameWithFigure(near.name, fmt(nearV, o))+'</small>' : '')+'</span>';
    };
    const bb = Object.keys(CONNECTIVITY_METRICS).filter((k) => !CONNECTIVITY_METRICS[k].mobile).map((k) => {
        const o = {...opts, metric:k};
        const v = connectivityValue(details, o);
        return has(v) ? row(CONNECTIVITY_METRICS[k].pill, 'data-metric="'+k+'"', k===opts.metric, v, o) : '';
    }).join('');
    if (bb) html += '<span class="head">Broadband · share of premises</span><div class="rows">'+bb+'</div>';
    if (details.mobile) {
        const measure = m.mobile ? opts.metric : 'mob4g';
        const mob = Object.keys(CONNECTIVITY_NETWORKS).map((n) => {
            const o = {...opts, metric:measure, network:n};
            const v = connectivityValue(details, o);
            return has(v) ? row(CONNECTIVITY_NETWORKS[n], 'data-network="'+n+'"', m.mobile && n===opts.network, v, o) : '';
        }).join('');
        if (mob) html += '<span class="head">'+CONNECTIVITY_METRICS[measure].pill+' · by network</span><div class="rows">'+mob+'</div>';
    }

    html += '<div class="source">Ofcom Connected Nations. Broadband: share of homes and businesses that can order each speed. '+
        'Mobile: share of premises with good coverage, from operators\' predictions, by local authority only.</div>';
    html += '</div>';
    return html;
};

// --- The national card of the maps without flows: the 5 highest and lowest areas ---

const RANKED_COUNT = 5;
const rankedCSS = '<style>'+
    '.areaMapPopup.ranked .places li.on {background:'+BRAND.tint+'; font-weight:600;} '+
    '.areaMapPopup.ranked .places b.up {color:#a3202f;} .areaMapPopup.ranked .places b.down {color:#1d5c99;} '+
    '.areaMapPopup.ranked.orange .places b.up {color:#1d5c99;} .areaMapPopup.ranked.orange .places b.down {color:#a14e05;} '+
    '.areaMapPopup.ranked .places li i {opacity:.9;} '+
    '</style>';

// Every area with a figure for the map's settings, highest first. Areas with none (crime
// where the police didn't publish every month, Scotland's rents, Wales's deprivation) are
// left out, as is a figure of 0, which means no data rather than none (a change of 0% is kept)
export const rankAreas = (features, def, opts) => {
    const isChange = def.isChange(opts);
    return features.map((f) => ({cd:f.areacd, nm:f.areanm, details:f.details || {}, value:def.getValue(f.details, opts)}))
        .filter((r) => has(r.value) && (isChange || Number(r.value)!==0))
        .map((r) => ({...r, value:Number(r.value)}))
        .sort((x, y) => y.value-x.value);
};

// The highest and lowest areas' lists (fewer when there are under 10, so none is in both)
export const rankedTopBottom = (rows) => {
    const n = Math.min(RANKED_COUNT, Math.floor(rows.length/2));
    return {top:rows.slice(0, n), bottom:rows.slice(rows.length-n).reverse()};
};

// The lists' headings: the map's own for a figure, by direction for a change
export const rankedHeads = (def, opts, top, bottom) => {
    if (!def.isChange(opts)) {
        const heads = (def.national && def.national.heads) ? def.national.heads(opts) : ['Highest', 'Lowest'];
        return heads;
    }
    const topHead = top.length && top.every((r) => r.value>0) ? 'Rose most'
        : top.length && top.every((r) => r.value<0) ? 'Fell least' : 'Highest change';
    const bottomHead = bottom.length && bottom.every((r) => r.value<0) ? 'Fell most'
        : bottom.length && bottom.every((r) => r.value>0) ? 'Rose least' : 'Lowest change';
    return [topHead, bottomHead];
};

// With no area picked, on desktop: the areas with the highest and lowest figures (or change)
// the map is shaded by, each in its colour on the map; picking one opens its popup
const rankedNationalHTML = (def) => (features, opts, isMobile, scale, pickedId) => {
    const nat = def.national || {};
    const isChange = def.isChange(opts);
    const rows = rankAreas(features, def, opts);
    const {top, bottom} = rankedTopBottom(rows);
    const [topHead, bottomHead] = rankedHeads(def, opts, top, bottom);
    const max = Math.max(1e-9, ...rows.map((r) => Math.abs(r.value)));
    const fmt = (r) => nat.format ? nat.format(r.value, opts, r.details) : def.format(r.value, opts);
    const list = (items) => items.length===0 ? '<span class="none">No figures</span>' :
        '<ul class="places">'+items.map((r) => '<li data-select="'+escapeHTML(r.cd)+'" role="button" title="See '+escapeHTML(r.nm)+'"'+
            (r.cd===pickedId ? ' class="on"' : '')+'>'+
            '<span>'+escapeHTML(r.nm)+'</span><b'+(isChange ? ' class="'+riseClass(r.value)+'"' : '')+'>'+fmt(r)+'</b>'+
            '<i style="width:'+Math.max(2, 100*Math.abs(r.value)/max).toFixed(1)+'%;background:'+(scale ? scale.colorOf(r.value) : BRAND.ink)+'"></i></li>').join('')+'</ul>';
    // What is shown: the key's title and the first part of its subtitle (not how it's shaded)
    const sample = (features[0] || {}).details;
    const what = def.legendTitle(opts)+' · '+String(def.legendSubtitle(opts, sample) || '').split(' · ')[0];
    let html = areaMapPopupCSS(isMobile)+migrationCSS+rankedCSS+
        '<div class="areaMapPopup mig ranked'+(def.changeColors==='fallOrangeRiseBlue' ? ' orange' : '')+'">';
    html += '<span class="tag">'+escapeHTML(def.menuShortText)+(isChange ? ' · change' : '')+'</span>';
    html += '<div class="title"><span class="name">'+escapeHTML(nat.place || 'England and Wales')+'</span></div>';
    html += '<span class="summary">'+escapeHTML(what)+'. '+(isMobile ? 'Tap' : 'Click')+' a '+def.areaName+' to see its figures.</span>';
    html += '<span class="head">'+escapeHTML(topHead)+'</span>'+list(top);
    html += '<span class="head">'+escapeHTML(bottomHead)+'</span>'+list(bottom);
    html += '<div class="source">Of '+rows.length+' '+def.areaNamePlural+' with figures. '+(nat.source || '')+'</div></div>';
    return html;
};

export const AREA_MAPS = {
    propertyLad: {
        mapName:'property_lad',
        menuText:'House prices across England and Wales',
        menuShortText:'House prices',
        menuIcon:PublicOutlined,
        title:'Sold prices by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        drillDown:{mapName:'property_msoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods'},
        controls:[
            {key:'metric', label:'Show', options:Object.fromEntries(Object.keys(PRICE_METRICS).map((k) => [k, PRICE_METRICS[k].pill]))},
            {key:'period', label:'Period', options:PRICE_PERIODS},
            {key:'category', label:'Home type', options:PRICE_TYPES},
        ],
        defaults:{metric:'medpricepersqm', period:'1y', category:'all'},
        getValue:(details, opts) => priceValue(details, opts.period, opts.category, opts.metric),
        isChange:isPriceChange,
        changeViews:true,
        changeYears:priceChangeYears,
        changeView:priceChangeView,
        setChangeView:setPriceChangeView,
        sinceText:priceSince,
        changeColors:'fallOrangeRiseBlue',
        format:(v, opts) => isPriceChange(opts) ? formatPct(v) : formatPriceLevel(v, opts.metric),
        legendTitle:(opts) => isPriceChange(opts) ? 'Change in '+PRICE_METRICS[opts.metric].name.replace('median ', '')
            : sentenceCase(PRICE_METRICS[opts.metric].name),
        legendSubtitle:(opts) => sentenceCase(PRICE_TYPES[opts.category].toLowerCase())+', '+
            (isPriceChange(opts) ? 'over '+PRICE_CHANGE_TEXT[opts.period]+changeLegendTextOrange
                : PRICE_LEVEL_TEXT[opts.period]+' · each shade is a seventh of areas'),
        popupHTML:propertyLadPopupHTML,
        national:{source:'Source: HM Land Registry Price Paid Data.'},
    },
    crimeLad: {
        mapName:'crime_lad',
        menuText:'Crime across England and Wales',
        menuShortText:'Crime',
        menuIcon:ShieldOutlined,
        title:'Crime by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        drillDown:{mapName:'crime_msoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods'},
        controls:[
            {key:'metric', label:'Show', options:Object.fromEntries(Object.keys(CRIME_METRICS).map((k) => [k, CRIME_METRICS[k].pill]))},
            {key:'category', label:'Crime', options:CRIME_CATEGORIES},
            CHANGE_CONTROL,
        ],
        defaults:{metric:'numcrimesper100', category:'total', change:'level'},
        palette:'rust',
        getValue:crimeValue,
        isChange:isViewChange,
        changeViews:true,
        changeYears:periodYears,
        changeView:(opts) => opts.change,
        setChangeView:(view) => ({change:view}),
        sinceText:(details, opts) => {
            const period = periodFor(details, opts);
            return period ? 'since '+period.maxdate : '';
        },
        changeColors:'fallRise',
        format:(v, opts) => isViewChange(opts) ? formatPct(v) : formatCrime(v, opts.metric),
        legendTitle:(opts) => isViewChange(opts) ? 'Change in '+crimeName(opts.category) : sentenceCase(crimeName(opts.category)),
        legendSubtitle:(opts) => isViewChange(opts) ?
            'Last 12 months against '+changeSpanText(opts)+' earlier'+changeLegendText
            : sentenceCase(CRIME_METRICS[opts.metric].name)+', last year · each shade is a seventh of areas',
        popupHTML:crimeLadPopupHTML,
        national:{heads:() => ['Most crime', 'Least crime'],
            source:'Source: data.police.uk. Areas whose police haven\'t published every month are left out.'},
    },
    deprivationLad: {
        mapName:'imd_lad',
        menuText:'Deprivation across England',
        menuShortText:'Deprivation',
        menuIcon:InsightsOutlined,
        title:'Deprivation by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        // Its neighbourhoods are ranked within the 2025 local authority, which ukmap.imd_lad_area
        // adds: the 2024 one's code but for Barnsley and Sheffield
        drillDown:{mapName:'imd_lsoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods',
            parentCode:(props) => (props.details && props.details.lad25cd) || props.areacd},
        controls:[
            {key:'domain', label:'Show', options:IMD_DOMAINS},
        ],
        defaults:{domain:'imdscore'},
        palette:'deprivation',
        getValue:deprivationLadValue,
        isChange:() => false,
        format:(v) => String(Math.round(v)),
        legendEnds:['Least deprived', 'Most'],
        legendTitle:(opts) => 'Deprivation · '+IMD_DOMAINS[opts.domain],
        legendSubtitle:() => 'English Indices of Deprivation 2025 · each shade is a seventh of areas',
        popupHTML:deprivationLadPopupHTML,
        // Higher is more deprived: shown as its rank, 1st the most deprived
        national:{place:'England', heads:() => ['Most deprived', 'Least deprived'],
            format:(v, opts, details) => {
                const r = details && details.rank ? Number(details.rank[opts.domain.replace('score', 'rank')]) : NaN;
                return has(r) ? ordinal(r) : String(Math.round(v));
            },
            source:'Ranked from 1st, the most deprived, by the population weighted average rank of their neighbourhoods. Source: English Indices of Deprivation 2025.'},
    },
    rentLad: {
        mapName:'rent_lad',
        menuText:'Rents across England and Wales',
        menuShortText:'Rents',
        menuIcon:KeyOutlined,
        title:'Private rents by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        controls:[
            {key:'type', label:'Home', options:RENT_TYPE_PILLS},
            CHANGE_CONTROL,
        ],
        // The kind of home is picked in the Filters panel, not on the map
        hoverTip:true,
        defaults:{type:'all', change:'level'},
        palette:'blue',
        getValue:rentValue,
        isChange:isViewChange,
        changeViews:true,
        changeYears:periodYears,
        changeView:(opts) => opts.change,
        setChangeView:(view) => ({change:view}),
        sinceText:rentSince,
        changeColors:'fallOrangeRiseBlue',
        format:formatRentFigure,
        legendTitle:(opts) => isViewChange(opts) ? 'Change in average monthly rent' : 'Average monthly rent',
        legendSubtitle:(opts) => sentenceCase(RENT_TYPES[opts.type].name)+(isViewChange(opts) ?
            ', latest month against '+changeSpanText(opts)+' earlier'+changeLegendTextOrange
            : ', latest month · each shade is a seventh of areas'),
        popupHTML:rentLadPopupHTML,
        national:{source:'Source: Office for National Statistics, Price Index of Private Rents. Scotland\'s council areas have no figures of their own.'},
    },
    salesRateLad: {
        mapName:'sales_rate_lad',
        menuText:'Sales per 100 people across England and Wales',
        menuShortText:'Sales rate',
        menuIcon:SwapHorizOutlined,
        title:'Home sales per 100 people by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        drillDown:{mapName:'sales_rate_msoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods'},
        controls:[
            {key:'type', label:'Home', options:SALES_RATE_PILLS},
            CHANGE_CONTROL,
        ],
        mapSwitch:'type',
        hoverTip:true,
        defaults:{type:'all', change:'level'},
        palette:'teal',
        getValue:salesRateValue,
        isChange:isViewChange,
        changeViews:true,
        changeYears:periodYears,
        changeView:(opts) => opts.change,
        setChangeView:(view) => ({change:view}),
        sinceText:salesRateSince,
        changeColors:'fallOrangeRiseBlue',
        format:formatSalesRateFigure,
        legendTitle:(opts) => isViewChange(opts) ? 'Change in homes sold' : 'Sales per 100 people',
        legendSubtitle:(opts) => sentenceCase(SALES_RATE_TYPES[opts.type].name)+(isViewChange(opts) ?
            ', 12 months against '+changeSpanText(opts)+' earlier'+changeLegendTextOrange
            : ', last 12 months of sales · each shade is a seventh of areas'),
        popupHTML:salesRateLadPopupHTML,
        national:{heads:() => ['Most sales per 100 people', 'Fewest sales per 100 people'],
            source:'Sales: HM Land Registry Price Paid Data. People: ONS small area population estimates.'},
    },
    connectivityLad: {
        mapName:'connectivity_lad',
        menuText:'Broadband and mobile across the UK',
        menuShortText:'Broadband',
        menuIcon:WifiOutlined,
        title:'Broadband and mobile coverage by local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        drillDown:{mapName:'connectivity_msoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods'},
        controls:[
            {key:'metric', label:'Show', options:CONNECTIVITY_PILLS},
            {key:'network', label:'Network', options:CONNECTIVITY_NETWORKS,
                note:'For the mobile figures. Ofcom publishes these by local authority only.'},
        ],
        mapSwitch:'metric',
        hoverTip:true,
        defaults:{metric:'gigabit', network:'all'},
        palette:'blue',
        getValue:connectivityValue,
        isChange:() => false,
        format:formatConnectivity,
        legendTitle:(opts) => connectivityWhat(opts),
        legendSubtitle:() => 'Ofcom Connected Nations · each shade is a seventh of areas',
        popupHTML:connectivityPopupHTML,
        national:{place:'United Kingdom',
            heads:(opts) => opts.metric==='under10' ? ['Most premises under 10 Mbit/s', 'Fewest'] : ['Best covered', 'Least covered'],
            source:'Source: Ofcom Connected Nations.'},
    },
    migrationLad: {
        mapName:'migration_lad',
        menuText:'Moving in and out across England and Wales',
        menuShortText:'Moving',
        menuIcon:MovingOutlined,
        title:'Moves between local authorities',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        controls:[
            {key:'age', label:'Age group', options:MIGRATION_AGES,
                note:'Recolours the map and the local authority\'s card. Arrows show all ages, as ONS doesn\'t split the moves between places by age.'},
            // Shown in the card, not the Filters panel
            {key:'arrows', label:'Arrows', hidden:true, options:{net:'Net moves', all:'All moves'}},
        ],
        defaults:{age:'all', arrows:'net'},
        hoverTip:true,
        getValue:migrationValue,
        isChange:() => false,
        buildScale:migrationScale,
        format:(v, opts) => formatSigned(v)+(opts && opts.age && opts.age!=='all' ? ' aged '+MIGRATION_AGES[opts.age] : ''),
        // The key's ends: the size of the deepest band's edge, either way
        legendRange:(scale) => scale.edges.length ? ['−'+plain.format(scale.edges[scale.edges.length-1])+'+', '0',
            '+'+plain.format(scale.edges[scale.edges.length-1])+'+'] : null,
        legendTitle:(opts) => opts.age!=='all' ? 'Net moves, ages '+MIGRATION_AGES[opts.age] : 'Net moves in and out',
        legendSubtitle:(opts, sample) => 'People who moved in less those who moved out'+
            (migrationPeriod(sample) ? ', '+migrationPeriod(sample) : '')+' · purple lost, green gained',
        // Clicking an area draws arrows to and from the places people moved between, and shows
        // its card beside the map rather than in a popup (AreaMapLayer.js, flowArrows.js)
        flows:migrationFlows,
        signedFlows:(opts) => opts.arrows!=='all',
        nationalHTML:migrationNationalHTML,
        clickHint:'to see who moves in and out',
        popupHTML:migrationLadPopupHTML,
    },
    // Opened from the Deprivation layer's popup, for the local authority named in it
    imdLad: {
        mapName:null,
        withinOnly:true,
        listed:false,
        menuText:'Deprivation in a local authority',
        menuShortText:'Deprivation',
        menuIcon:PublicOutlined,
        title:'Deprivation by neighbourhood within a local authority',
        areaName:'local authority',
        areaNamePlural:'local authorities',
        drillDown:{mapName:'imd_lsoa', areaName:'neighbourhood', areaNamePlural:'neighbourhoods'},
        controls:[
            {key:'domain', label:'Show', options:IMD_DOMAINS},
        ],
        defaults:{domain:'imdscore'},
        palette:'deprivation',
        getValue:imdLadValue,
        isChange:() => false,
        format:(v) => String(Math.round(v)),
        legendEnds:['Least deprived', 'Most'],
        legendTitle:(opts) => 'Deprivation · '+IMD_DOMAINS[opts.domain],
        legendSubtitle:() => 'Ranked within the local authority · each shade is a seventh of areas',
        popupHTML:imdLsoaPopupHTML,
    },
};

// The maps with a national card of their highest and lowest areas
Object.values(AREA_MAPS).forEach((def) => {
    if (def.national && !def.nationalHTML) def.nationalHTML = rankedNationalHTML(def);
});

export const areaMapLayers = Object.keys(AREA_MAPS);
// The maps the menus offer
export const listedAreaMaps = areaMapLayers.filter((k) => AREA_MAPS[k].listed!==false);

// A map's settings: the user's choices over its defaults, dropping any that are no
// longer one of its options
export const areaMapOptions = (layer, saved) => {
    const def = AREA_MAPS[layer];
    const opts = {...def.defaults};
    def.controls.forEach((c) => {
        if (saved && saved[c.key]!==undefined && c.options.hasOwnProperty(saved[c.key])) opts[c.key] = saved[c.key];
    });
    // The area a withinOnly map shows
    if (def.withinOnly && saved) {
        ['parent', 'parentName'].forEach((k) => { if (saved[k]) opts[k] = saved[k]; });
    }
    return opts;
};

